// Offline image queue for WIN Field App.
// Stores image blobs in IndexedDB when uploads fail offline.
// Enforces a storage budget and purges stale/uploaded blobs.
//
// Storage rules:
//   - Budget: 200MB pending images max (fallback constant)
//   - Purge is caller-owned: an uploaded blob is marked "uploaded" on a confirmed
//     200 but is NOT deleted here. The caller purges (purgeUploaded) only AFTER it
//     has durably persisted the substituted server URL (ADR-0048 §3 -- purge and
//     URL-substitution must be atomic).
//   - Pending blobs are NEVER time-purged -- they are the only copy of captured
//     field work until they upload (confirmed 200) or the user explicitly
//     discards them. A tasker may be offline > 48h (ADR-0048 §3).
//   - purgeStale() is a safety net: it sweeps confirmed-"uploaded" orphans older
//     than 48h that a caller never got to purge (e.g. app died after persisting).
//   - Soft ceiling derived from device quota (50% of quota, <= 2 GB, fallback 200 MB);
//     crossing it raises a warning flag but NEVER refuses capture (W4, ADR-0048 §6)

import db from "./field_db"
import { logEvent, logError, logWarn, logInfo, getDroppedLogCount, getPlatformSnapshot } from "./field_logger"
import { classifySyncResponse } from "./field_sync_shared"
import { observedFetch, currentLevel, recordSample, classifyFetchOutcome, isTimeoutError, LEVEL_DEGRADED } from "./field_network_health"
import { readCsrfToken } from "./field_session"
import { timeoutFor } from "./field_timeout_policy"
import { supportsUploadProgress, uploadWithProgress } from "./lib/xhr_upload"
import {
  isVaultRow, writeToVault, readFromVault, deleteFromVault, VAULT_REASON
} from "./field_photo_vault"
import { shouldLogOnce } from "./field_log_throttle"

// Test-only observability seam (Card #3492 regression fix) -- NOT used by any
// application code. esbuild bundles each app/javascript/*.js entry point
// separately, inlining every relative import into that entry's own output
// (see field_degraded_mode_local.spec.js's header comment for the full
// explanation), so this file's compiled bundle carries its OWN isolated copy
// of field_network_health.js's module state. A test that separately imports
// field_network_health.js's own bundle to call evidenceSnapshot() would be
// reading a DIFFERENT instance and would never see the samples _uploadImage's
// XHR path below records. Re-exporting it here gives test/pwa specs a way to
// prove that path actually feeds the same evidence stream observedFetch would
// have -- from the one bundle instance where that call really happens.
export { evidenceSnapshot } from "./field_network_health"

// Test-only observability/controllability seam (Card #3798) -- NOT used by
// any application code. Same reasoning as evidenceSnapshot above: this
// bundle's `db` import is its OWN isolated Dexie instance, so a test can only
// reach the ONE _uploadImage actually calls through by going via THIS
// bundle's own export, not by importing field_db.js separately. Re-exported
// as a plain reference (no behaviour change) so test/pwa specs can
// temporarily monkeypatch e.g. `_db.pending_images.update` to simulate a
// write failure at a specific point in the upload flow -- proving
// _uploadImage's outer safety net (see below) logs and recovers rather than
// leaving a record stranded in "uploading".
export { default as _db } from "./field_db"

const BUDGET_BYTES = 200 * 1024 * 1024   // 200 MB fallback
const SOFT_CEILING_FRACTION = 0.5        // 50% of device quota
const SOFT_CEILING_MAX_BYTES = 2 * 1024 * 1024 * 1024  // cap at 2 GB
// Last-resort safety net only. The PRIMARY purge path is response-sync-gated
// (purgeUploaded on confirmed replay; purgeShadowForResponse on confirmed online
// submit). This net sweeps confirmed-uploaded orphans whose owning caller died
// before purging. It is deliberately far beyond 48h so a tasker offline for days
// never has a confirmed-uploaded record reclaimed out from under an in-flight
// substitution -- and it NEVER touches "queued" captures at any age (ADR-0048 §3).
const STALE_TTL_MS = 7 * 24 * 60 * 60 * 1000  // 7 days

// Grace period for submitted images. purgeForResponse stamps submittedAt on
// confirmed-uploaded queued captures instead of hard-deleting them on mission
// submit; purgeSubmitted sweeps them once the grace lapses. The image stays
// recoverable and visible as "Submitted" in My Photos for a day.
export const SUBMITTED_GRACE_MS = 24 * 60 * 60 * 1000

// A network failure is never terminal (card #3827): the photo is healthy, the
// signal isn't. retryCount keeps climbing for telemetry and the wait stays on
// the last slot once it gets there, so a weak-signal day can't strand
// photos the way 5 strikes used to (tasker #76). Card #3844 -- the ceiling is
// 1 hour (was 4), matching rt-client, and a reconnect or a Sync tap clears the
// wait altogether (clearUploadBackoff).
const BACKOFF_MS = [60_000, 300_000, 900_000, 3_600_000] // 1min, 5min, 15min, 1hr

// Sentinel failStatus for a blob that failed the readability probe (Card #2601).
// Deliberately NOT 0 -- failStatus 0 reads as "offline" in statusForRecord and
// displays as "pending" in the gallery (field_photos_controller._displayState),
// implying it'll clear once signal returns. An unreadable blob never will, no
// matter how many times it's retried, so it must surface as a genuine
// "validation"-style "failed" instead (any non-zero value does this).
export const BLOB_UNREADABLE_FAIL_STATUS = -1

// failStatus for a photo the server refused to store for size (card #3990: the
// 50MB hard ceiling). The server answers 2xx file_received:false, reason
// "too_large"; unlike a truncated upload that can never succeed on retry, so
// the row is parked "failed" (not retried by any automatic pass, blob kept on
// the device, shown as failed in My Photos) instead of looping every hour.
export const TOO_LARGE_FAIL_STATUS = 413

// A record only reaches "uploading" immediately before a fetch (_uploadImage,
// backupOrphan). If the tab/SW dies mid-flight (crash, force-close, OS kill)
// nothing else resets it, and syncImagesFor() deliberately SKIPS "uploading"
// records -- so a stuck one would sit invisible and unsynced forever (Card
// #2419). 5 minutes is generous headroom above the 30s upload fetch timeout
// while still recovering promptly.
const STUCK_UPLOAD_TIMEOUT_MS = 5 * 60 * 1000

// Returns the soft-ceiling in bytes for pending images, derived from device quota.
// Uses 50% of navigator.storage.estimate() quota, capped at 2 GB.
// Falls back to BUDGET_BYTES (200 MB) if the Storage API is unavailable.
async function deviceSoftCeiling() {
  try {
    const estimate = await navigator?.storage?.estimate()
    if (estimate?.quota) {
      return Math.min(estimate.quota * SOFT_CEILING_FRACTION, SOFT_CEILING_MAX_BYTES)
    }
  } catch { /* not available */ }
  return BUDGET_BYTES
}

// Request durable storage to reduce browser eviction risk. Called from the main
// app (field_photo_vault_controller) as well as the offline shell, and logged
// once per page load so a phone that refuses is visible by platform (card
// #3995). Never throws. Resolves { supported, alreadyPersisted, granted }.
export async function requestPersistentStorage() {
  const result = { supported: false, alreadyPersisted: false, granted: false }
  try {
    const storage = navigator?.storage
    if (!storage?.persist) {
      logInfo("storage", "Persistent storage unsupported", { platform: getPlatformSnapshot().os }).catch(() => {})
      return result
    }
    result.supported = true
    result.alreadyPersisted = (await storage.persisted?.().catch(() => false)) === true
    result.granted = result.alreadyPersisted || (await storage.persist()) === true
  } catch { /* not available */ }

  const platform = getPlatformSnapshot()
  const write = result.granted ? logInfo : logWarn
  write("storage", result.granted ? "Persistent storage granted" : "Persistent storage not granted", {
    platform:         platform.os,
    displayMode:      platform.displayMode,
    alreadyPersisted: result.alreadyPersisted
  }).catch(() => {})
  return result
}

// Register the Background Sync tag so the service worker uploads when signal
// returns. Fire-and-forget safe: never throws, may never resolve when the SW
// is unavailable, so callers should NOT await it on a critical path.
export async function registerUploadSync() {
  try {
    const reg = await navigator.serviceWorker?.ready
    await reg?.sync?.register('field-upload-sync')
  } catch { /* Background Sync not supported -- foreground triggers cover it */ }
}

// A direct library upload: banked from My Photos with no owning response.
export function isDirectUpload(record) {
  return record?.responseId == null && record?.surveyableId === "0"
}

// -- photo bytes held in memory (card #3994) --
//
// On iPhones a photo that has been through IndexedDB often cannot be read back
// (UnknownError "Error preparing Blob/File data to be stored in object store",
// NotFoundError), sometimes within seconds of being queued. The File made at
// capture is still perfectly good, so it is kept here for the session and every
// upload attempt (the first one at capture, then retries and sync passes) sends
// THESE bytes first. The stored blob is only read when memory no longer has the
// photo (the app restarted).
//
// Keyed by the capture id (the queue row's blobKey). Capped so a long session
// cannot hold unbounded photo data: past the cap the OLDEST photo is evicted and
// falls back to its stored copy. A photo is released only after the server
// confirmed it (a 2xx that does not say file_received:false), or when its queue
// row is purged.
//
// Card #3995 -- a photo whose stored copy is not yet verified (the vault write
// has not finished, or failed its read-back) is the only good copy there is, so
// it is never evicted, whatever the cap. Every eviction is logged.
const MEMORY_CAP_BYTES = 20 * 1024 * 1024
const _memoryPhotos = new Map()    // captureId -> File, oldest first
const _memoryUploads = new Set()   // captureIds with an attempt in flight
const _unverified = new Set()      // captureIds with no verified stored copy yet
const _vaultFailed = new Set()     // captureIds whose vault write failed (warned)

// Background upload state (card #4097, see startBackgroundUpload below). Declared
// here because holdPhotoInMemory never evicts a photo with an attempt in flight.
const _backgroundJobs = new Map()    // captureId -> job, until the photo lands or can never upload
const _backgroundActive = new Set()  // captureIds with an attempt in flight
const _backgroundWaiting = []        // captureIds waiting for a free slot, oldest first

export function newCaptureId() {
  return `img_${Date.now()}_${Math.random().toString(36).slice(2)}`
}

// unverified: true marks a photo whose stored copy is not yet verified (see
// above); it is protected from eviction until markPhotoVerified or release.
export function holdPhotoInMemory(captureId, file, { unverified = false } = {}) {
  if (!captureId || !(file instanceof Blob) || file.size > MEMORY_CAP_BYTES) return false

  _memoryPhotos.delete(captureId)
  _memoryPhotos.set(captureId, file)
  if (unverified) _unverified.add(captureId)

  let total = 0
  for (const held of _memoryPhotos.values()) total += held.size
  for (const key of [..._memoryPhotos.keys()]) {
    if (total <= MEMORY_CAP_BYTES) break
    if (key === captureId || _unverified.has(key) || _memoryUploads.has(key) || _backgroundActive.has(key)) continue
    const size = _memoryPhotos.get(key).size
    total -= size
    _memoryPhotos.delete(key)
    logInfo("upload", "Photo evicted from memory", {
      platform:   getPlatformSnapshot().os,
      blobKey:    key,
      sizeBytes:  size,
      heldBytes:  total,
      capBytes:   MEMORY_CAP_BYTES
    }).catch(() => {})
  }
  return true
}

export function heldPhoto(captureId) {
  return _memoryPhotos.get(captureId) || null
}

export function releasePhoto(captureId) {
  _memoryPhotos.delete(captureId)
  _unverified.delete(captureId)
  if (_vaultFailed.delete(captureId)) _announceVaultFailures()
}

// The stored copy of this photo passed its read-back: memory may evict it.
export function markPhotoVerified(captureId) {
  _unverified.delete(captureId)
}

// How many photos held in memory have no verified stored copy yet (a photo is
// briefly in this state during every capture).
export function unverifiedPhotoCount() {
  return _unverified.size
}

// How many photos the vault could not store and that now exist only in memory.
// The warning banner stays up while this is above zero.
export function vaultFailedCount() {
  return _vaultFailed.size
}

// Whether the place is flagged Poor Signal (places.bad_connectivity), from the
// device's own place cache. Never throws; unknown means false.
export async function placeIsPoorSignal(placeId) {
  const id = Number(placeId)
  if (!Number.isFinite(id) || id <= 0) return false
  try {
    const place = await db.cached_places.get(id)
    return !!place?.bad_connectivity
  } catch {
    return false
  }
}

// Timeout for an upload attempt from memory. Poor Signal forces the longer
// Degraded value whatever the measured level says: the flag is ops telling us
// the store has weak coverage, so the attempt is given time to land rather than
// being skipped (card #3994).
export function memoryUploadTimeoutMs(poorSignal) {
  return timeoutFor("image_upload_memory", poorSignal ? LEVEL_DEGRADED : currentLevel())
}

// "Photo upload from memory" -- one line per attempt made from in-memory bytes,
// whether at capture ("capture") or from a retry/sync pass ("retry"), so the
// watch command can see the effect of this path by platform.
function _logMemoryUpload(outcome, { source, timeoutMs, poorSignal, durationMs, status, sizeBytes }) {
  logEvent("upload", "Photo upload from memory", {
    outcome,
    source,
    platform:   getPlatformSnapshot().os,
    timeoutMs,
    poorSignal: !!poorSignal,
    durationMs: Math.round(durationMs),
    status:     status ?? null,
    sizeBytes:  sizeBytes ?? null
  }).catch(() => {})
}

// The first upload attempt at capture, made from the bytes still in memory and
// touching IndexedDB not at all. Never throws. Returns:
//   { ok: true,  data }                       -- the server has the photo
//   { ok: false, kind, status?, statusText?, message?, data? }
//     kind "timeout" | "network" | "no_csrf"  -- no answer (nothing reached us)
//          "http"                              -- a non-2xx answer
//          "not_attached"                      -- a 2xx that carries no photo
//                                                 (file_received:false, or an
//                                                 orphaned bank with no url)
// The caller queues the photo on any !ok. A 2xx that does not say
// file_received:false is the only thing that releases the in-memory bytes
// (card #3990 contract: file_received:false means "keep your copy").
//
// capture_id is sent so a retry after a timeout that actually landed gets the
// same Image back instead of a second one (card #3841). The caller queues the
// photo under the SAME captureId on failure.
//
// timeoutMs overrides the capture timeout (the background uploader's rowless
// fallback uses the background timeouts, card #4097).
export async function uploadPhotoFromMemory(file, { captureId, surveyableId, surveyableType, csrfToken, poorSignal = false, timeoutMs: timeoutOverride = null }) {
  const timeoutMs = timeoutOverride || memoryUploadTimeoutMs(poorSignal)
  const startedAt = _now()
  const report = (outcome, status) => _logMemoryUpload(outcome, {
    source: "capture", timeoutMs, poorSignal, durationMs: _now() - startedAt, status, sizeBytes: file.size
  })

  // Nothing is stored yet: this copy is the only one until
  // the queue write is verified (or the server confirms), so it is not evicted.
  holdPhotoInMemory(captureId, file, { unverified: true })
  _memoryUploads.add(captureId)
  try {
    const token = csrfToken || document.querySelector("[name='csrf-token']")?.content || await readCsrfToken()
    if (!token) {
      report("failed")
      return { ok: false, kind: "no_csrf" }
    }

    // Identifiers BEFORE the multi-MB file (card #2864): a request truncated
    // mid-stream still arrives attributable.
    const formData = new FormData()
    formData.append("surveyable_id",   String(surveyableId))
    formData.append("surveyable_type", surveyableType || "Response")
    if (captureId) formData.append("capture_id", captureId)
    formData.append("file", file, file.name || "image.webp")

    let response
    try {
      response = await observedFetch("/api/v1/surveys/files/upload", {
        method:  "POST",
        headers: { "X-CSRF-Token": token, Accept: "application/json" },
        body:    formData,
        signal:  AbortSignal.timeout(timeoutMs)
      }, { kind: "image_upload", sizeClass: "large" })
    } catch (error) {
      const timedOut = isTimeoutError(error)
      report(timedOut ? "timeout" : "failed")
      return { ok: false, kind: timedOut ? "timeout" : "network", message: error?.message || null }
    }

    if (!response.ok) {
      report("failed", response.status)
      return { ok: false, kind: "http", status: response.status, statusText: response.statusText, response }
    }

    const data = await response.json().catch(() => null)
    if (!data?.url || data.file_received === false || data.retain_local === true) {
      report("failed", response.status)
      return { ok: false, kind: "not_attached", status: response.status, data: data || {} }
    }

    releasePhoto(captureId)
    report("success", response.status)
    return { ok: true, data }
  } finally {
    _memoryUploads.delete(captureId)
  }
}

// -- background upload at capture (card #4097, field:background_photo_upload) --
//
// With the flag on, capture no longer waits for the upload. The photo is held
// in memory (the #3994 rule stands: every attempt sends the in-memory bytes
// first), its bytes are written once to the vault and a metadata-only queue row
// is added straight away (card #3995, _storeCapture), so the sync bar, My Photos
// and the drain-on-open path can all see the photo, and the upload runs here
// without anyone awaiting it. The caller gets a local blob: URL back at once.
//
// Each attempt goes through _uploadImage, the same code the queue retries
// use, so every outcome (2xx, file_received:false, 4xx, network error,
// timeout) is handled exactly as a queued photo's would be. At most
// BACKGROUND_CONCURRENCY attempts run at a time (decision D2); the rest wait
// in order, as rows in "pending", and the sync passes leave them alone. These
// attempts do not go through _inSequence (one at a time for retry passes): the
// two slots here are the decided concurrency for photos just taken.
//
// "Background" means "while the page is open and awake". iOS stops a request
// when the app is hidden; an attempt that ends that way is re-armed with no
// backoff and started again when the page is visible (_resumeBackgroundUploads).
const BACKGROUND_CONCURRENCY = 2
// Full Jitter floor (decision D7): never retry sooner than this.
const JITTER_FLOOR_MS = 5_000
let _lastHiddenAt = 0

// Timeout for an attempt nobody waits on (decision D3): 60s, or 90s at a Poor
// Signal place or on a degraded connection.
export function backgroundUploadTimeoutMs(poorSignal) {
  return timeoutFor("image_upload_background", poorSignal ? LEVEL_DEGRADED : currentLevel())
}

// Start a photo's upload without waiting for it. Returns { captureId, localUrl }
// straight away; the vault write, the row write and the upload carry on behind.
// Never throws. The answer should hold localUrl until field:photo-landed says
// otherwise.
export function startBackgroundUpload(file, { captureId, surveyableId, surveyableType, responseId, placeId, clientId, questionName, poorSignal = false } = {}) {
  const id = captureId || newCaptureId()
  const localUrl = URL.createObjectURL(file)
  // Nothing is stored yet: this copy is the only one until the vault write is
  // verified, so it is not evicted (card #3995).
  holdPhotoInMemory(id, file, { unverified: true })

  const job = {
    captureId:      id,
    localUrl,
    recordId:       null,
    surveyableId:   String(surveyableId),
    surveyableType: surveyableType || "Response",
    responseId:     responseId != null ? String(responseId) : String(surveyableId),
    placeId:        placeId != null && placeId !== "" ? String(placeId) : null,
    clientId:       clientId != null && clientId !== "" ? String(clientId) : null,
    questionName:   questionName || null,
    poorSignal:     !!poorSignal,
    sizeBytes:      file.size,
    attempts:       0
  }
  job.ready = _writeBackgroundRow(file, job)
  _backgroundJobs.set(id, job)
  _enqueueBackground(id)
  return { captureId: id, localUrl }
}

// "active" (an attempt is in flight), "waiting" (queued for a free slot), or
// null (not running here: landed, waiting for signal, or never started here).
export function backgroundUploadState(captureId) {
  if (_backgroundActive.has(captureId)) return "active"
  if (_backgroundWaiting.includes(captureId)) return "waiting"
  return null
}

// Photos of this response that are sending right now or waiting for a slot.
// What the completion gate's Wait button waits on.
export function backgroundBusyCount(surveyableId) {
  return backgroundBusyIds(surveyableId).length
}

// The capture ids behind backgroundBusyCount, with each photo's size, so the
// gate can count a photo whose queue row is not written yet.
export function backgroundBusyIds(surveyableId) {
  const busy = []
  for (const job of _backgroundJobs.values()) {
    if (job.surveyableId === String(surveyableId) && backgroundUploadState(job.captureId)) {
      busy.push({ captureId: job.captureId, sizeBytes: job.sizeBytes })
    }
  }
  return busy
}

// The completion gate's Wait (card #4097): photos of this response captured on
// this page that are waiting for signal are tried again now, ignoring their
// backoff, the same as a Sync tap does for the queue. Returns how many started.
export async function retryBackgroundNow(surveyableId) {
  let started = 0
  for (const job of [..._backgroundJobs.values()]) {
    if (job.surveyableId !== String(surveyableId) || backgroundUploadState(job.captureId) || job.recordId == null) continue
    const record = await db.pending_images.get(job.recordId).catch(() => null)
    if (record?.status !== "pending") continue
    if (record.nextRetryAt) await safeSetStatus(record.id, { nextRetryAt: null })
    _enqueueBackground(job.captureId)
    started++
  }
  return started
}

function _enqueueBackground(captureId) {
  if (backgroundUploadState(captureId)) return
  _backgroundWaiting.push(captureId)
  _announceBackgroundState(captureId)
  _pumpBackground()
}

function _pumpBackground() {
  while (_backgroundActive.size < BACKGROUND_CONCURRENCY && _backgroundWaiting.length > 0) {
    const captureId = _backgroundWaiting.shift()
    const job = _backgroundJobs.get(captureId)
    if (!job) continue
    _backgroundActive.add(captureId)
    _announceBackgroundState(captureId)
    _runBackgroundJob(job)
      .catch(error => {
        logError("upload", "Background photo upload failed unexpectedly", {
          blobKey: captureId, name: error?.name, message: error?.message
        }).catch(() => {})
      })
      .finally(() => {
        _backgroundActive.delete(captureId)
        _announceBackgroundState(captureId)
        _pumpBackground()
      })
  }
}

// The bytes go once into the vault and the row is metadata only (card #3995,
// _storeCapture), marked background so its retries use the background timeout
// and Full Jitter. A failed vault or row write is not fatal: the photo is still
// in memory, the tasker gets the vault warning, and the attempt goes ahead
// (from memory, with or without a row -- _runBackgroundJob).
async function _writeBackgroundRow(file, job) {
  const stored = await _storeCapture(job.captureId, file, _backgroundRowFields(job))
  job.recordId = stored.id
  if (stored.vaultFailed) _markVaultFailed(job.captureId)
  // Android: lets the service worker finish the upload if the page dies.
  if (stored.id != null) registerUploadSync().catch(() => {})
}

function _backgroundRowFields(job) {
  return {
    surveyableId:   job.surveyableId,
    surveyableType: job.surveyableType,
    responseId:     job.responseId,
    placeId:        job.placeId,
    clientId:       job.clientId,
    questionName:   job.questionName,
    kind:           "queued",
    localUrl:       job.localUrl,
    serverUrl:      null,
    status:         "pending",
    queuedAt:       new Date().toISOString(),
    uploadedAt:     null,
    sizeBytes:      job.sizeBytes,
    background:     true,
    poorSignal:     job.poorSignal
  }
}

async function _runBackgroundJob(job) {
  await job.ready
  job.attempts++
  const source = job.attempts === 1 ? "capture" : "retry"

  if (job.recordId == null) return _runRowlessBackgroundJob(job, source)

  const record = await db.pending_images.get(job.recordId)
  if (!record) {
    // Discarded (the tasker removed the photo) while it waited.
    _backgroundJobs.delete(job.captureId)
    return
  }
  if (record.status === "uploaded" || (isLost(record) && !heldPhoto(record.blobKey))) {
    _finishBackgroundJob(job, record)
    return
  }

  const token = document.querySelector("[name='csrf-token']")?.content || await readCsrfToken()
  await _uploadImage(record, token, { source })
  const fresh = await db.pending_images.get(job.recordId).catch(() => null)
  _finishBackgroundJob(job, fresh)
}

// A photo whose queue row could not be written: one attempt from memory, and if
// that does not land, one more try at storing it (vault plus row, carrying the
// same blob: link the answer holds) so the ordinary queue can retry it.
async function _runRowlessBackgroundJob(job, source) {
  const file = heldPhoto(job.captureId)
  if (!file) {
    _backgroundJobs.delete(job.captureId)
    logError("upload", "Photo lost before upload: no row and no bytes in memory", { blobKey: job.captureId }).catch(() => {})
    return
  }
  const attempt = await uploadPhotoFromMemory(file, {
    captureId:      job.captureId,
    surveyableId:   job.surveyableId,
    surveyableType: job.surveyableType,
    poorSignal:     job.poorSignal,
    timeoutMs:      backgroundUploadTimeoutMs(job.poorSignal)
  })
  if (attempt.ok) {
    _backgroundJobs.delete(job.captureId)
    _announceLanded(job, attempt.data.url)
    return
  }
  const stored = await _storeCapture(job.captureId, file, _backgroundRowFields(job))
  if (stored.id == null) {
    logError("upload", "Photo queue fallback after row write failure failed", {
      blobKey: job.captureId, source
    }).catch(() => {})
  } else {
    if (!stored.vaultFailed) _clearVaultFailed(job.captureId)
    registerUploadSync().catch(() => {})
  }
  _backgroundJobs.delete(job.captureId)
}

// After an attempt: a landed photo is announced and forgotten; a lost or
// refused one is forgotten (My Photos and the chip show it); a photo still
// waiting for signal stays tracked so it restarts when the page is visible.
function _finishBackgroundJob(job, record) {
  if (!record) {
    _backgroundJobs.delete(job.captureId)
    return
  }
  if (record.status === "uploaded" && record.serverUrl) {
    _backgroundJobs.delete(job.captureId)
    _announceLanded(job, record.serverUrl)
  } else if (record.status === "failed") {
    _backgroundJobs.delete(job.captureId)
  }
}

function _announceLanded(job, serverUrl) {
  if (typeof window === "undefined") return
  try {
    window.dispatchEvent(new CustomEvent("field:photo-landed", {
      detail: { captureId: job.captureId, localUrl: job.localUrl, serverUrl, surveyableId: job.surveyableId, questionName: job.questionName }
    }))
  } catch { /* never block an upload */ }
}

function _announceBackgroundState(captureId) {
  if (typeof window === "undefined") return
  try {
    window.dispatchEvent(new CustomEvent("field:photo-state", { detail: { captureId, state: backgroundUploadState(captureId) } }))
  } catch { /* never block an upload */ }
}

// True when an attempt that started at startedAtMs (epoch) ended because the
// app was hidden: iOS stops requests in a hidden page, which says nothing
// about the signal.
function _abortedWhileHidden(startedAtMs) {
  if (typeof document === "undefined") return false
  return document.hidden === true || _lastHiddenAt >= startedAtMs
}

// Back to visible: restart every photo this page captured that a hidden-page
// abort left pending with no wait. Photos in a real backoff keep it.
async function _resumeBackgroundUploads() {
  for (const job of _backgroundJobs.values()) {
    if (backgroundUploadState(job.captureId) || job.recordId == null) continue
    const record = await db.pending_images.get(job.recordId).catch(() => null)
    if (record?.status === "pending" && !record.nextRetryAt) _enqueueBackground(job.captureId)
  }
}

if (typeof document !== "undefined" && typeof document.addEventListener === "function") {
  document.addEventListener("visibilitychange", () => {
    if (document.hidden) {
      _lastHiddenAt = Date.now()
    } else {
      _resumeBackgroundUploads().catch(() => {})
    }
  })
}

// Queue an OFFLINE capture for upload. The stored copy is the ONLY durable copy
// of this field work until it uploads (confirmed 200) -- so kind is "queued" and
// it is NEVER time-purged (ADR-0048 §3, W1). Returns a local blob: URL for
// immediate display, plus overBudget (callers may warn but capture is never
// refused, W4) and vaultFailed (see below).
//
// context carries the richer record fields banked on every capture (W1):
//   placeId, clientId, responseId, questionName -- any may be undefined/null.
//
// blobKey (optional) is the capture id an upload attempt at capture already used
// (uploadPhotoFromMemory): the queued retry then carries the same capture_id, so
// the server can match it to a photo that landed after the attempt timed out.
//
// Card #3995 -- the bytes are written ONCE to
// pending_blobs and read straight back (length and SHA-256), and the
// pending_images row is metadata only, so no later status write can touch the
// bytes. vaultFailed is true when that stored copy could not be verified (or the
// row itself could not be written): the photo then exists only in memory, the
// caller must warn the tasker, and one upload from memory is tried straight
// away. Capture is never blocked and never refused.
//
// needsHeicConversion (card #4914) -- true when `file` is the RAW, unconverted
// HEIC original: field_offline_app_controller.js sets this when
// compressImageWithRetry couldn't convert it because the lazily-loaded HEIC
// decoder (heic_decoder.js, a SECONDARY install asset since #4916) isn't
// cached yet -- no signal, and the decoder was never fetched at install. The
// photo is banked as-is rather than dropped; convertPendingHeicPhotos
// (below) converts it on a later upload attempt, once a decoder is available.
export async function queueImage(file, { surveyableId, surveyableType, placeId, clientId, responseId, questionName, blobKey: captureId, needsHeicConversion = false } = {}) {
  const blobKey = captureId || newCaptureId()
  const localUrl = URL.createObjectURL(file)

  const stored = await _storeCapture(blobKey, file, {
    surveyableId:   String(surveyableId),
    surveyableType: surveyableType || "Response",
    responseId:     responseId != null ? String(responseId) : String(surveyableId),
    placeId:        placeId   != null ? String(placeId)   : null,
    clientId:       clientId  != null ? String(clientId)  : null,
    questionName:   questionName || null,
    kind:           "queued",
    localUrl,
    serverUrl:      null,
    status:         "pending",
    queuedAt:       new Date().toISOString(),
    uploadedAt:     null,
    sizeBytes:      file.size,
    needsHeicConversion
  })

  const overBudget = await _isOverSoftCeiling()
  logEvent("upload", "Photo queued offline", { surveyableId, sizeBytes: file.size }).catch(() => {})

  if (stored.vaultFailed) {
    _markVaultFailed(blobKey)
    _retryFromMemorySoon(blobKey)
  }

  // Register a Background Sync tag so the service worker can upload when signal returns
  await registerUploadSync()

  return { blobKey, localUrl, overBudget, vaultFailed: stored.vaultFailed }
}

// Bank a DIRECT library upload (My Photos "Upload from your library") with NO
// owning response. surveyableId "0" + responseId null mark it as a direct
// upload so sync routes it to the backup endpoint. kind is "queued": the stored
// copy is the only one until a confirmed 2xx, never time-purged (ADR-0048 §3).
// Dexie indexes skip the null responseId -- intentional: purgeForResponse can
// never claim it. Does NOT register background sync itself (the caller decides,
// to avoid a foreground/SW double-upload race). Returns the Dexie record id.
//
// placeId/clientId carry the batch Place + Client chosen in the My Photos form
// before upload -- stamped on every record so both the foreground backup and
// any later SW replay send the same context.
//
// Stored through the same write-once vault as queueImage (card #3995);
// vaultFailed means the caller should warn and carry on uploading from memory.
export async function queueDirectUpload(file, { placeId, clientId } = {}) {
  const blobKey = newCaptureId()
  const localUrl = URL.createObjectURL(file)
  const stored = await _storeCapture(blobKey, file, {
    surveyableId:   "0",
    surveyableType: "Response",
    responseId:     null,
    placeId:        placeId  != null ? String(placeId)  : null,
    clientId:       clientId != null ? String(clientId) : null,
    questionName:   null,
    kind:           "queued",
    localUrl,
    serverUrl:      null,
    status:         "pending",
    queuedAt:       new Date().toISOString(),
    uploadedAt:     null,
    sizeBytes:      file.size
  })
  logEvent("upload", "Library photo queued", { sizeBytes: file.size }).catch(() => {})
  if (stored.vaultFailed) _markVaultFailed(blobKey)
  return { id: stored.id, blobKey, localUrl, vaultFailed: stored.vaultFailed }
}

// The one place a captured photo is written to the queue. Holds the bytes in
// memory first (so a failure below never loses them), then writes
// pending_blobs once, verifies it, and adds a metadata-only row.
// Never throws. Resolves { id, vaultFailed }; id is null when the row itself
// could not be added.
async function _storeCapture(blobKey, file, row) {
  holdPhotoInMemory(blobKey, file, { unverified: true })

  const vault = await writeToVault(blobKey, file)

  const record = {
    blobKey,
    blob:       null,
    fileName:   file.name || null,
    blobStore:  "vault",
    vaultState: vault.ok ? "verified" : "failed",
    sha256:     vault.ok ? vault.sha256 : null,
    byteLength: vault.ok ? vault.byteLength : null,
    ...row
  }

  let id = null
  try {
    id = await db.pending_images.add(record)
  } catch (error) {
    // Nothing queued: the photo exists only in memory (and the vault, if that
    // write held). The upload from memory below is its only way out.
    logError("upload", "Photo queue row could not be written", {
      platform: getPlatformSnapshot().os,
      blobKey,
      name:     error?.name,
      message:  error?.message
    }).catch(() => {})
    return { id: null, vaultFailed: true }
  }

  if (vault.ok) markPhotoVerified(blobKey)
  return { id, vaultFailed: !vault.ok }
}

// Tells the page a photo could not be stored safely, so it can show the plain
// warning (field_photo_vault_controller, banner partial). A window event, so
// nothing here builds HTML and capture never waits for the page to react.
//
// The event is fired whenever the number of such photos changes, and carries the
// number in detail.failed: it rises when a photo could not be stored and falls
// to zero once each has reached the server (or been discarded), which clears
// the warning.
export const VAULT_WARNING_EVENT = "field:photo-vault-warning"
function _announceVaultFailures() {
  try {
    window.dispatchEvent(new CustomEvent(VAULT_WARNING_EVENT, { detail: { failed: _vaultFailed.size } }))
  } catch { /* no window (service worker) */ }
}
function _markVaultFailed(captureId) {
  if (captureId) _vaultFailed.add(captureId)
  _announceVaultFailures()
}
// A photo that was only in memory now has a verified stored copy (the
// background uploader's second store attempt, card #4097).
function _clearVaultFailed(captureId) {
  if (_vaultFailed.delete(captureId)) _announceVaultFailures()
}

// One immediate upload from memory for a photo the vault could not keep, via the
// same path a sync pass uses (so the row ends "uploaded" with its server URL
// and the normal URL substitution picks it up). Fire and forget: capture never
// waits for it. A success clears the in-memory copy, which clears the warning.
function _retryFromMemorySoon(blobKey) {
  if (typeof navigator !== "undefined" && navigator.onLine === false) return
  setTimeout(async () => {
    try {
      const record = await db.pending_images.where("blobKey").equals(blobKey).first()
      if (!record || !heldPhoto(blobKey)) return
      const csrfToken = document.querySelector("[name='csrf-token']")?.content || await readCsrfToken()
      if (!csrfToken) return
      await _inSequence(() => _uploadImage(record, csrfToken))
    } catch { /* the warning is already showing; the sync pass retries it */ }
  }, 0)
}

// Bank a SHADOW backup of an ONLINE capture (W1, ADR-0048). The image has
// ALREADY been uploaded to the server (the caller holds the server URL); this
// is purely a local recovery copy in case the response never durably syncs.
// kind is "shadow", status is "uploaded" from the outset (server has it), and
// serverUrl is recorded. Shadow rows are purged once their response is confirmed
// synced (purgeShadowForResponse), with purgeStale as a far-future last resort.
//
// Returns the local blob: URL of the banked copy (the survey itself stores the
// SERVER url -- this localUrl is only for the recovery copy's own bookkeeping).
export async function bankShadowImage(file, { surveyableId, surveyableType, serverUrl, placeId, clientId, responseId, questionName } = {}) {
  const blobKey = `img_${Date.now()}_${Math.random().toString(36).slice(2)}`
  const localUrl = URL.createObjectURL(file)

  await db.pending_images.add({
    blobKey,
    blob:           file,
    surveyableId:   String(surveyableId),
    surveyableType: surveyableType || "Response",
    responseId:     responseId != null ? String(responseId) : String(surveyableId),
    placeId:        placeId   != null ? String(placeId)   : null,
    clientId:       clientId  != null ? String(clientId)  : null,
    questionName:   questionName || null,
    kind:           "shadow",
    localUrl,
    serverUrl:      serverUrl || null,
    status:         "uploaded",
    queuedAt:       new Date().toISOString(),
    uploadedAt:     new Date().toISOString(),
    sizeBytes:      file.size
  })

  logEvent("upload", "Photo shadow-banked", { surveyableId, sizeBytes: file.size }).catch(() => {})

  return { blobKey, localUrl }
}

// Upload all pending images for a given surveyable.
// Marks each successfully-uploaded record as "uploaded" but does NOT purge it --
// purge is the caller's job once the substituted server URL is durably persisted
// (ADR-0048 §3). Returns:
//   { urlMap:      { localUrl -> serverUrl } to substitute into survey data,
//     uploadedIds: [record id, ...] uploaded this pass or before,
//     attachedIds: the subset the server confirmed it wrote into the
//                  response's answer at upload (card #3827) -- already durable,
//                  safe to purge. The rest of uploadedIds may only be purged
//                  once the caller confirms the answer holds the server URL
//                  (ADR-0054). }
export async function syncImagesFor(surveyableId, csrfToken) {
  const startedAt = _now()
  const all = await db.pending_images
    .where("surveyableId").equals(String(surveyableId))
    .toArray()

  const urlMap = {}
  const uploadedIds = []
  const attachedIds = []

  // Per-pass counters (Card #3799) -- a stalled queue previously showed up
  // server-side as missing events, not as a visible reason. skippedBackoff
  // and skippedUploading are the two silent branches that used to log nothing
  // at all.
  let attempted = 0
  let uploaded = 0
  let failed = 0
  let skippedBackoff = 0
  let skippedUploading = 0
  let skippedLost = 0

  for (const record of all) {
    if (record.status === "uploaded" && record.serverUrl) {
      // Already on the server -- either an offline-queued record uploaded by a
      // prior pass / syncOrphanedImages, or a shadow backup of an online capture.
      // Map its URL (harmless no-op for shadow rows, whose localUrl never appears
      // in online survey data) and mark it eligible for purge once the response
      // is confirmed synced (W2).
      urlMap[record.localUrl] = record.serverUrl
      uploadedIds.push(record.id)
      if (isAttached(record)) attachedIds.push(record.id)
    } else if (_memoryUploads.has(record.blobKey) || backgroundUploadState(record.blobKey)) {
      // An attempt from memory is already in flight for this photo, or it is
      // waiting for a slot in the background uploader (card #4097).
      skippedUploading++
    } else if (isLost(record) && !heldPhoto(record.blobKey)) {
      // Card #3827 -- the bytes are unreadable or gone. Retrying can only fail
      // again (and used to, every pass); My Photos asks for a retake instead.
      // (A lost row whose photo is still held in memory is retried: the bytes
      // are not gone, only the stored copy is -- card #3994.)
      skippedLost++
    } else if (record.status === "pending" || record.status === "failed" || record.status === "reauth-required") {
      // Skip records still in backoff. "reauth-required" records never carry
      // nextRetryAt -- they retry on every pass once the tasker is signed
      // back in (Card #2417), same mechanism as a fresh "pending" record.
      if (record.nextRetryAt && new Date(record.nextRetryAt) > new Date()) {
        skippedBackoff++
        continue
      }
      attempted++
      const serverUrl = await _inSequence(() => _uploadImage(record, csrfToken))
      if (serverUrl) {
        uploaded++
        urlMap[record.localUrl] = serverUrl
        uploadedIds.push(record.id)
        const fresh = await db.pending_images.get(record.id).catch(() => null)
        if (isAttached(fresh)) attachedIds.push(record.id)
      } else {
        failed++
      }
    } else if (record.status === "uploading") {
      // In flight from another context (another tab, or the SW) -- leave alone.
      skippedUploading++
    }
  }

  // Only worth a line when something other than "everything was already
  // uploaded" happened this pass -- a no-op pass over already-synced records
  // would otherwise log on every single sync cycle.
  if (attempted > 0 || skippedBackoff > 0 || skippedUploading > 0 || skippedLost > 0) {
    logInfo("upload", "Image sync pass", {
      surveyableId,
      attempted,
      uploaded,
      failed,
      skippedBackoff,
      skippedUploading,
      skippedLost,
      durationMs: Math.round(_now() - startedAt)
    }).catch(() => {})
  }

  return { urlMap, uploadedIds, attachedIds }
}

// True when the upload response confirmed the server wrote this record's URL
// into its response's answer (card #3827, _uploadImage's attachedAt).
export function isAttached(record) {
  return !!record?.attachedAt && record?.status === "uploaded" && !!record?.serverUrl
}

// Read every at-risk local row for the gallery union (W3). "At-risk" means a
// blob that is NOT yet confirmed safe on WIN:
//   - "queued" captures still pending/uploading/failed (the only copy of that
//     field work), and
//   - "shadow" backups not yet purged (their response hasn't confirmed synced).
//
// A confirmed-uploaded "queued" row (status "uploaded") is in-flight to purge
// and still counts as on-device until purgeUploaded fires, so it is included too.
// Shadow rows always carry status "uploaded" (server has them) -- they are
// included because the response isn't confirmed until purgeShadowForResponse.
//
// Returns the raw records; the gallery maps them to render rows and derives the
// per-image status label.
export async function atRiskImages() {
  return db.pending_images.toArray()
}

// Map a pending_images record to its visible status for the gallery (ADR-0048 §1):
//   "submitted"   -- response submitted; auto-clears after the grace period
//   "uploading"   -- a queued capture mid-upload
//   "lost"        -- the photo's bytes can't be read back (Card #2601), or were
//                    dropped because they couldn't be written (card #3827). It
//                    can never upload; the tasker needs to take it again.
//   "failed"      -- a real rejection from the server (4xx); not retried
//   "safe"        -- a shadow backup (server already has it; awaiting response
//                    confirm) or a queued row confirmed uploaded (awaiting purge)
//   "on_device"   -- a queued capture still waiting to upload. Includes a row
//                    an old build marked failed after 5 network errors
//                    (failStatus 0) -- it is re-armed on the next sync pass
//                    (rearmNetworkExhausted), so it is not a failure.
export function statusForRecord(record) {
  if (record.submittedAt) return { state: "submitted" }
  if (record.kind === "shadow") return { state: "safe" }
  if (record.status === "uploading") return { state: "uploading" }
  if (record.status === "uploaded") return { state: "safe" }
  if (isLost(record)) return { state: "lost" }
  if (record.status === "failed" && record.failStatus !== 0) return { state: "failed", reason: "validation" }
  return { state: "on_device" }
}

// A row whose photo can never upload: its blob failed the readability probe
// or was dropped by safeSetStatus. Never retried by any sync path.
export function isLost(record) {
  return record?.status === "failed" && record?.failStatus === BLOB_UNREADABLE_FAIL_STATUS
}

// The bytes of a queued photo as a Blob for display (gallery thumbnails, queue
// thumbnails), or null when there is nothing readable to show. Vault rows are
// read from pending_blobs (no hash check: this runs on every render); legacy
// rows hand back their embedded blob. Never throws.
export async function photoPreviewBlob(record) {
  try {
    if (isVaultRow(record)) {
      const result = await readFromVault(record.blobKey, { verify: false })
      return result.ok ? result.file : null
    }
    return record?.blob instanceof Blob ? record.blob : null
  } catch {
    return null
  }
}

// Blob-safe status write (card #3827, reworked for the vault in card #3995).
// Dexie's Table.update reads the row and puts ALL of it back. For a row that
// carries its own Blob that rewrite is the hazard: on a real iPhone (iOS 26.5,
// 8 Oct 2026) a Blob written by a process that has since died stays readable
// until the row holding it is rewritten, and then it is not. (This used to be
// put down to Chromium: tasker #157's 88 photos, "UnknownError: Error
// preparing Blob/File data to be stored in object store". Every device in the
// card's evidence is iOS.)
//
// Vault rows (blobStore "vault") hold no bytes, so a status write on them is a
// plain metadata write and can never harm a photo. If one throws it is retried
// once and reported; nothing is dropped, ever.
//
// Legacy rows (an embedded blob, written before the vault) keep the old safety
// net. Callers read the blob BEFORE their first status write (resolveSendBlob),
// so a copy is in memory by the time this runs. Then, if the normal update throws,
// the row is read back and its blob probed:
//   - blob readable: the failure wasn't the blob (e.g. a transient "connection
//     is closing"). Retries once; if that fails too, reports it. A healthy
//     photo is never dropped.
//   - blob unreadable (or already gone): rewrites the row WITHOUT its blob --
//     same id, every other field kept (responseId, questionName, sizeBytes,
//     queuedAt, kind, localUrl...) so it can still be counted and shown in My
//     Photos. Unless the change records a confirmed upload, the row becomes
//     "lost" (failed, BLOB_UNREADABLE_FAIL_STATUS): there is nothing left to
//     upload.
// Never throws -- this runs on every sync path. Returns
// { ok, blobDropped, error } (error: the write failure when ok is false).
export async function safeSetStatus(id, changes) {
  let updateError
  try {
    await db.pending_images.update(id, changes)
    return { ok: true, blobDropped: false }
  } catch (error) {
    updateError = error
  }

  try {
    const record = await db.pending_images.get(id)
    if (!record) return { ok: false, blobDropped: false, error: updateError }

    if (isVaultRow(record) || await _blobReadable(record.blob)) {
      try {
        await db.pending_images.update(id, changes)
        return { ok: true, blobDropped: false }
      } catch (retryError) {
        _logOncePerHour(`status_write_failed:${record.blobKey}`, logError, "Photo status write failed", {
          blobKey:         record.blobKey,
          requestedStatus: changes.status || null,
          name:            retryError?.name,
          message:         retryError?.message
        })
        return { ok: false, blobDropped: false, error: retryError }
      }
    }

    const now = new Date().toISOString()
    const uploaded = (changes.status || record.status) === "uploaded" && !!(changes.serverUrl || record.serverUrl)
    const saved = {
      ...record,
      ...changes,
      ...(uploaded ? {} : { status: "failed", failedAt: now, failStatus: BLOB_UNREADABLE_FAIL_STATUS }),
      blob:          null,
      blobDroppedAt: now
    }
    await db.transaction("rw", db.pending_images, async () => {
      await db.pending_images.delete(id)
      await db.pending_images.add(saved)
    })

    // Lost for good -> error (a human should find it). Already on the server
    // -> warn (degraded but recovered).
    const write = uploaded ? logWarn : logError
    write("upload", "Photo blob dropped", {
      blobKey:         saved.blobKey,
      responseId:      saved.responseId || null,
      questionName:    saved.questionName || null,
      sizeBytes:       saved.sizeBytes || null,
      requestedStatus: changes.status || null,
      savedStatus:     saved.status,
      name:            updateError?.name,
      message:         updateError?.message
    }).catch(() => {})
    return { ok: true, blobDropped: true }
  } catch (rewriteError) {
    _logOncePerHour(`status_write_failed:id${id}`, logError, "Photo status write failed", {
      id,
      requestedStatus: changes.status || null,
      updateName:      updateError?.name,
      updateMessage:   updateError?.message,
      name:            rewriteError?.name,
      message:         rewriteError?.message
    })
    return { ok: false, blobDropped: false, error: rewriteError }
  }
}

// The bytes to send for a record, in the order that never writes first:
//   1. memory (the File captured this session -- card #3994)
//   2. the vault, for a vault row (read back and checked, card #3995)
//   3. the legacy embedded blob, read IN FULL now and held in memory, so the
//      status writes that follow cannot cost the photo even though they rewrite
//      the row. The bytes are also moved into the vault and
//      the row made metadata-only, so the next rewrite has nothing to harm.
// Never throws. Resolves { ok: true, blob, source } or { ok: false, reason,
// errorName, errorMessage }.
async function resolveSendBlob(record) {
  const memoryFile = heldPhoto(record.blobKey)
  if (memoryFile) return { ok: true, blob: memoryFile, source: "memory" }

  if (isVaultRow(record)) {
    const result = await readFromVault(record.blobKey, { name: record.fileName || "image.webp" })
    return result.ok ? { ok: true, blob: result.file, source: "vault" } : result
  }

  const stored = record.blob
  if (!(stored instanceof Blob)) return { ok: false, reason: VAULT_REASON.MISSING }
  try {
    await stored.slice(0, 1).arrayBuffer()
    const buffer = await stored.arrayBuffer()
    const file = new File([buffer], stored.name || "image.webp", { type: stored.type })
    holdPhotoInMemory(record.blobKey, file)
    await _migrateLegacyRow(record, file)
    return { ok: true, blob: file, source: "legacy" }
  } catch (error) {
    return { ok: false, reason: VAULT_REASON.SOURCE_UNREADABLE, errorName: error?.name || null, errorMessage: error?.message || null }
  }
}

// Moves a legacy row's bytes into the vault. The bytes are
// verified in the vault BEFORE the row is rewritten, and the rewrite sets blob
// to null rather than putting the old Blob back. If either step fails the row
// is left as it was; the in-memory copy still carries this attempt.
async function _migrateLegacyRow(record, file) {
  const vault = await writeToVault(record.blobKey, file)
  if (!vault.ok) return
  try {
    await db.pending_images.update(record.id, {
      blob:       null,
      fileName:   file.name || null,
      blobStore:  "vault",
      vaultState: "verified",
      sha256:     vault.sha256,
      byteLength: vault.byteLength
    })
    markPhotoVerified(record.blobKey)
  } catch { /* row stays legacy; the vault copy is swept if it is never used */ }
}

// The same 1-byte read _uploadImage uses as its readability probe.
async function _blobReadable(blob) {
  if (!(blob instanceof Blob)) return false
  try {
    await blob.slice(0, 1).arrayBuffer()
    return true
  } catch {
    return false
  }
}

// Back up ONE at-risk banked blob to WIN as a recovery copy, even if its response
// never syncs (W4, ADR-0048). Uploads the blob to the orphan-backup endpoint with
// the banked context. On a confirmed 200 the local row is purged (it is now safe
// on WIN) -- purge ONLY after the durable 200, never before (W2 semantics reused).
//
// Returns:
//   { ok: true,  imageId, url, reattached }  -- banked; local row purged
//   { ok: false, retainLocal: true, reason } -- not banked; local copy kept
export async function backupOrphan(recordId, csrfToken) {
  const record = await db.pending_images.get(recordId)
  if (!record) return { ok: false, retainLocal: true, reason: "missing" }
  // Card #3827 -- no bytes to send (dropped by safeSetStatus, or never
  // readable). Nothing to back up; the row stays for My Photos to show.
  // Card #3994 -- bytes still held in memory are sent in preference to the
  // stored blob (which an iPhone often cannot read back), so a row whose stored
  // blob is gone is still backed up while memory has the photo.
  const memoryFile = heldPhoto(record.blobKey)
  if (isLost(record) && !memoryFile) return { ok: false, retainLocal: true, reason: "unreadable" }
  // Card #3995 -- bytes in hand before the first status write (vault bytes
  // checked against their hash; a legacy blob read in full and held in memory).
  const resolved = await resolveSendBlob(record)
  if (!resolved.ok) return { ok: false, retainLocal: true, reason: "unreadable" }
  const sendBlob = resolved.blob
  // Card #3990 addendum -- never send a zero-byte blob; mark it lost (retake).
  if (sendBlob.size === 0) {
    await safeSetStatus(record.id, { status: "failed", failedAt: new Date().toISOString(), failStatus: BLOB_UNREADABLE_FAIL_STATUS })
    logError("upload", "Orphan backup refused: blob is zero bytes", { responseId: record.responseId || null }).catch(() => {})
    return { ok: false, retainLocal: true, reason: "unreadable" }
  }

  // Context fields BEFORE the file, same as _uploadImage below (card #2864)
  // -- a truncated multipart body still arrives attributable.
  const formData = new FormData()
  if (record.responseId)   formData.append("response_id",   record.responseId)
  if (record.placeId)      formData.append("place_id",      record.placeId)
  if (record.clientId)     formData.append("client_id",     record.clientId)
  if (record.questionName) formData.append("question_name", record.questionName)
  // Card #3827 -- the exact blob: slot this photo fills. Only meaningful
  // alongside question_name (a direct library upload has neither).
  if (record.questionName && record.localUrl) formData.append("local_url", record.localUrl)
  if (isDirectUpload(record)) formData.append("direct", "true")
  // Card #3841 -- this capture's id, so a retry after a lost response gets
  // the Image the server already stored instead of a second one.
  if (record.blobKey) formData.append("capture_id", record.blobKey)
  formData.append("file", sendBlob, sendBlob.name || "image.webp")

  // Mark in-flight so a concurrent SW background-sync pass (syncImagesFor skips
  // "uploading" records) can't double-upload the same record while this
  // foreground backup runs. Restored on failure; the success path purges.
  // uploadStartedAt lets recoverStuckUploads() reclaim this if the tab dies
  // before the restore/purge below ever runs (Card #2419).
  const prevStatus = record.status
  await safeSetStatus(record.id, { status: "uploading", uploadStartedAt: new Date().toISOString() })

  let response
  try {
    response = await observedFetch("/api/v1/surveys/files/backup", {
      method: "POST",
      headers: { "X-CSRF-Token": csrfToken, Accept: "application/json" },
      credentials: "include",
      body: formData,
      signal: AbortSignal.timeout(timeoutFor("image_upload", currentLevel()))
    }, { kind: "image_upload", sizeClass: "large" })
  } catch {
    await safeSetStatus(record.id, { status: prevStatus })
    logError("upload", "Orphan backup failed: network error", {
      responseId: record.responseId || null
    }).catch(() => {})
    return { ok: false, retainLocal: true, reason: "offline" }
  }

  const outcome = await classifySyncResponse(response)

  if (outcome === "reauth-required") {
    // Session expired mid-backup (Card #2417) -- distinct from a real
    // failure so it is retried automatically once the tasker signs back in,
    // rather than needing them to notice and re-tap "back up" manually.
    await safeSetStatus(record.id, {
      status: "reauth-required",
      reauthAt: new Date().toISOString(),
      failStatus: response.status
    })
    logEvent("upload", "Orphan backup paused — sign in required", {
      responseId: record.responseId || null, status: response.status
    }).catch(() => {})
    return { ok: false, retainLocal: true, reason: "reauth" }
  }

  if (outcome === "success") {
    let data
    try {
      data = await response.json()
    } catch {
      // Should be unreachable (classifySyncResponse already confirmed a real
      // JSON body), but a record must never be left stuck "uploading" (Card
      // #2419) -- restore rather than trust that guarantee blindly.
      await safeSetStatus(record.id, { status: prevStatus })
      return { ok: false, retainLocal: true, reason: "failed" }
    }
    // Card #3990 -- the server did not receive the file (truncated body, or
    // over its hard ceiling): keep the local copy, never purge.
    if (data?.file_received === false || data?.retain_local === true) {
      await safeSetStatus(record.id, { status: prevStatus })
      logError("upload", "Orphan backup: server did not store the file", {
        responseId: record.responseId || null, reason: data.reason || null
      }).catch(() => {})
      return { ok: false, retainLocal: true, reason: data.reason === "too_large" ? "too_large" : "not_received" }
    }
    // Banked as an OrphanedSync (no derivable client): safe on WIN, purge.
    if (data?.status === "orphaned") {
      await _purgeRecord(record.id)
      logEvent("upload", "Orphan image banked as orphaned sync", {
        responseId: record.responseId || null, orphanedSyncId: data.orphaned_sync_id || null,
        reason: data.reason || null, flags: data.flags || []
      }).catch(() => {})
      return { ok: true, banked: true, orphanedSyncId: data.orphaned_sync_id || null, reattached: false }
    }
    // Confirmed safe on WIN -- now (and only now) purge the local copy. Same
    // rule as the confirm path: no blob is deleted before its server copy is
    // durably recorded (ADR-0048 §3).
    await _purgeRecord(record.id)
    logEvent("upload", "Orphan image backed up", { responseId: record.responseId || null }).catch(() => {})
    return { ok: true, imageId: data.image_id, url: data.url, reattached: data.reattached }
  }

  // Could not attribute / validation failure -- KEEP the local copy. The photo
  // stays at-risk, which is honest: we banked what we could.
  await safeSetStatus(record.id, { status: prevStatus })
  let reason = "failed"
  try {
    const data = await response.json()
    if (data.retain_local) reason = "unattributed"
  } catch { /* ignore parse error */ }
  logError("upload", `Orphan backup failed (${response.status})`, {
    responseId: record.responseId || null, status: response.status, reason
  }).catch(() => {})
  return { ok: false, retainLocal: true, reason }
}

// Purge specific uploaded records by id, AFTER the caller has durably persisted
// their resolved server URL. Single owner of purge timing on the confirm path
// (ADR-0048 §3). purgeStale() remains the safety net.
export async function purgeUploaded(ids) {
  for (const id of ids || []) {
    await _purgeRecord(id)
  }
}

// Purge the SHADOW backups for a response once that response is confirmed synced
// to the server (W2 point 3). Online submits store the server image URL directly
// in survey data and leave a shadow recovery copy in pending_images; that copy is
// only safe to delete once the response's own PUT/complete has succeeded online.
//
// Only "shadow" rows are removed -- offline "queued" captures for the same
// response (the only copy of that work) are left untouched.
export async function purgeShadowForResponse(responseId) {
  if (responseId == null) return 0
  const shadows = await db.pending_images
    .where("responseId").equals(String(responseId))
    .filter(r => r.kind === "shadow")
    .toArray()

  for (const record of shadows) {
    await _purgeRecord(record.id)
  }
  return shadows.length
}

// Clean up the vault for a response once that response is confirmed durably on
// the server (online submit 200). Supersedes purgeShadowForResponse on the
// submit path -- it handles BOTH kinds (ADR-0048 §3):
//   - "shadow" rows: the server has both the image and the response; the local
//     recovery copy has done its job -- purge immediately.
//   - "queued" rows already uploaded (status "uploaded" + serverUrl): the bytes
//     are on the server but the row was banked offline, so rather than hard-
//     delete we stamp submittedAt and let purgeSubmitted reclaim it after the
//     grace period. My Photos shows it as "Submitted" until then.
//   - "queued" rows still pending/uploading/failed: the only copy of that field
//     work -- left untouched.
export async function purgeForResponse(responseId) {
  if (responseId == null) return { purged: 0, marked: 0 }
  const rows = await db.pending_images
    .where("responseId").equals(String(responseId))
    .toArray()

  let purged = 0
  let marked = 0
  const now = new Date().toISOString()

  for (const record of rows) {
    if (record.kind === "shadow") {
      await _purgeRecord(record.id)
      purged++
    } else if (record.status === "uploaded" && record.serverUrl && !record.submittedAt) {
      await safeSetStatus(record.id, { submittedAt: now })
      marked++
    }
  }

  return { purged, marked }
}

// Check if pending image bytes are under the device-aware soft ceiling.
export async function isUnderBudget() {
  const total = await _pendingBytes()
  const ceiling = await deviceSoftCeiling()
  return total < ceiling
}

// Run on app open. Last-resort safety net only: purges orphaned blobs that are
// BOTH confirmed-uploaded (status "uploaded") AND older than STALE_TTL_MS. The
// response-sync-gated paths (purgeUploaded, purgeShadowForResponse) are the
// primary cleanup, so this should rarely find anything.
//
// Pending/"queued" captures are NEVER time-purged regardless of age (W1,
// ADR-0048 §3). The kind guard makes that explicit: even a queued capture that
// has already uploaded keeps kind "queued", so this net leaves it alone and lets
// the response-sync-gated purgeUploaded (or syncOrphanedImages) reclaim it. In
// practice this net therefore only ever reclaims orphaned "shadow" rows whose
// online response never confirmed and purgeShadowForResponse never fired.
//
// Card #3827 (B1) -- also sweeps "queued" captures that are uploaded AND
// attached (attachedAt set: the server wrote them into the answer) more than
// STALE_TTL_MS ago. Those are now kept after upload until the response is
// confirmed submitted; for a response that never is on this device
// (abandoned, finished elsewhere, reassigned) nothing else would ever reclaim
// them. A queued capture that is not yet uploaded, or uploaded but never
// confirmed in an answer, is still never time-purged.
export async function purgeStale() {
  const cutoff = new Date(Date.now() - STALE_TTL_MS).toISOString()
  const stale = await db.pending_images
    .where("queuedAt").below(cutoff)
    .filter(r => r.status === "uploaded" && (r.kind !== "queued" || (!!r.attachedAt && r.attachedAt < cutoff)))
    .toArray()

  for (const record of stale) {
    await _purgeRecord(record.id)
  }

  // Card #3799 -- this was console.warn only, invisible server-side. Only
  // log when there's actually something to report; this runs on every
  // survey page open and almost always finds nothing (it's a last-resort
  // safety net, W2/§3 own the primary purge paths).
  if (stale.length > 0) {
    logInfo("upload", "Purged stale orphaned images", { count: stale.length }).catch(() => {})
  }

  // Card #3995 -- vault bytes left without a row.
  await purgeOrphanedVaultBlobs()

  return stale.length
}

// Sweep submitted images past the grace period. Runs on app open alongside
// purgeStale. Only rows that are BOTH confirmed-uploaded (status "uploaded" +
// serverUrl) AND stamped submittedAt by purgeForResponse are eligible -- a
// pending capture can never carry submittedAt, but the status guard makes the
// no-purge-before-confirmation invariant explicit (ADR-0048 §3).
export async function purgeSubmitted(olderThanMs = SUBMITTED_GRACE_MS) {
  const cutoff = Date.now() - olderThanMs
  const rows = await db.pending_images
    .filter(r => !!r.submittedAt && r.status === "uploaded" && !!r.serverUrl)
    .toArray()

  let purged = 0
  for (const record of rows) {
    if (new Date(record.submittedAt).getTime() <= cutoff) {
      await _purgeRecord(record.id)
      purged++
    }
  }
  return purged
}

// Recover any pending_images record left in "uploading" past
// STUCK_UPLOAD_TIMEOUT_MS. A record only reaches "uploading" immediately
// before a fetch (_uploadImage / backupOrphan); if the tab or service worker
// died mid-flight (crash, force-close, OS kill) nothing else resets it, and
// syncImagesFor() deliberately skips "uploading" records -- so a stuck record
// would otherwise sit unsynced forever (Card #2419). Resets to "pending" so
// it is picked up again on the very next sync pass. Run this on app open
// alongside purgeStale/purgeSubmitted (field_sync_controller.js connect()).
export async function recoverStuckUploads(olderThanMs = STUCK_UPLOAD_TIMEOUT_MS) {
  const cutoff = Date.now() - olderThanMs
  const stuck = await db.pending_images
    .where("status").equals("uploading")
    .filter(r => !r.uploadStartedAt || new Date(r.uploadStartedAt).getTime() <= cutoff)
    .toArray()

  for (const record of stuck) {
    try {
      // Card #3798 -- this used to be console.warn only, invisible to the same
      // app_logs pipeline every other sync outcome reports through. A record
      // only reaches here because SOMETHING left it stuck mid-upload (crash,
      // force-close, OS kill, or -- before this card's xhr_upload.js fix -- an
      // aborted XHR request that never settled its promise), so it belongs in
      // the same diagnostic stream as every other upload outcome.
      // Card #3995 -- one row per record per hour: a record that keeps
      // sticking used to log this on every pass (about 12,800 entries in four
      // days on iOS).
      _logOncePerHour(`recover:${record.blobKey}`, logEvent, "Recovering stuck upload", {
        blobKey: record.blobKey,
        surveyableId: record.surveyableId,
        uploadStartedAt: record.uploadStartedAt || null
      })
      // Card #3827 -- blob-safe: a row whose blob can't be written back is
      // rewritten without it (and marked lost) instead of throwing here and
      // aborting recovery for every row after it.
      await safeSetStatus(record.id, { status: "pending", nextRetryAt: null })
    } catch (error) {
      _logOncePerHour(`recover_failed:${record.blobKey}`, logError, "Recovering stuck upload failed", {
        blobKey: record.blobKey,
        name:    error?.name,
        message: error?.message
      })
    }
  }

  return stuck.length
}

// Card #3844 -- clears the retry wait on photos still waiting to upload, so a
// photo that failed while the signal was bad goes on the very next pass once
// it's back (UAT: a photo sat behind a 5 minute wait after reconnecting, and
// the Sync button couldn't override it). Called by field_sync_controller.js
// for an offline-to-online transition and a tap on Sync -- never for the
// automatic passes, which keep honouring the backoff. retryCount is kept.
export async function clearUploadBackoff(trigger) {
  const waiting = await db.pending_images
    .where("status").anyOf(["pending", "reauth-required"])
    .filter(r => !!r.nextRetryAt)
    .toArray()

  for (const record of waiting) {
    await safeSetStatus(record.id, { nextRetryAt: null })
  }

  if (waiting.length > 0) {
    logEvent("upload", "Cleared photo retry wait", { trigger, count: waiting.length }).catch(() => {})
  }
  return waiting.length
}

// Card #3827 -- re-arm photos an older build gave up on after 5 network
// errors (status "failed", failStatus 0). Their blobs are healthy; only the
// signal was bad (tasker #76). Back to "pending" with no backoff so the very
// next pass tries them. A real HTTP rejection (failStatus > 0) and an
// unreadable blob (BLOB_UNREADABLE_FAIL_STATUS) are left alone. Run inside the
// sync lock, beside recoverStuckUploads (field_sync_controller.js _runSync).
export async function rearmNetworkExhausted() {
  const exhausted = await db.pending_images
    .where("status").equals("failed")
    .filter(r => r.failStatus === 0)
    .toArray()

  for (const record of exhausted) {
    await safeSetStatus(record.id, { status: "pending", nextRetryAt: null, failStatus: null, failedAt: null })
  }

  if (exhausted.length > 0) {
    logEvent("upload", "Re-armed photos stopped by network errors", { count: exhausted.length }).catch(() => {})
  }
  return exhausted.length
}

// Age threshold past which a queued (offline) capture's own age makes the
// snapshot warn-worthy, regardless of anything else (Card #3799) -- this is
// the exact "192 iOS photos sat unsent for days" shape the card exists to
// catch.
const SNAPSHOT_STALE_HOURS = 24

// Re-arm window for the fingerprint throttle below. An UNCHANGED queue state
// still re-logs after this long even with no state change at all, so a
// genuinely stuck queue keeps surfacing periodically rather than throttling
// itself into silence forever -- the one failure mode that would defeat the
// whole point of this snapshot.
const SNAPSHOT_THROTTLE_MS = 60 * 60 * 1000 // 1 hour

const SNAPSHOT_SESSION_KEY = "queue_snapshot_fingerprint"

// Log a compact, queryable summary of the whole pending_images queue --
// counts by status and kind, the oldest queued capture's age, and how many
// client-side logs have been dropped to a full storage quota. This is the
// server-observable "queue snapshot" the card is named for: from
// list_app_logs alone, SIGNAL can say for any tasker how many photos are
// waiting, what state they're in, and why the last pass didn't upload them --
// without waiting for a tasker to notice and report it.
//
// info normally; warn when the oldest queued capture is >= 24h old, or
// something is stuck "uploading", or something is "failed" (any kind), or
// a photo has no live response to attach to (noLive), or logs are being
// dropped locally (logsDropped > 0) -- any of these is worth a human's
// attention on its own.
//
// Card #3827 keys (short, the detail stays under 600 chars):
//   noLive    -- rows still holding UNSENT work (pending, uploading,
//                reauth-required or failed; never an uploaded, shadow or
//                submitted-grace row) with no live response to attach to:
//                direct uploads, or
//                a responseId in neither cached_responses nor
//                pending_responses, or cached as "ended" (the prefetch only
//                ever serves kept, not-ended responses, so "ended" is the one
//                terminal status a cached row can hold)
//   lost / failNet / fail4xx -- "failed" split: unreadable or dropped blob
//                (isLost), network (failStatus 0, re-armed by
//                rearmNetworkExhausted), any other (a real HTTP rejection)
//   mb        -- queued bytes, rounded to a whole MB (rounded so the throttle
//                fingerprint doesn't change with every byte)
//   responses -- distinct responses the queue holds photos for
//
// Throttled by a fingerprint of the queue state stored in db.session (the
// same key/value table field_session.js already uses to hand the CSRF token
// to the service worker) -- an unchanged snapshot is skipped within
// SNAPSHOT_THROTTLE_MS of the last one, but always re-logs on any real change,
// and always re-arms after the window regardless, so a persistently stuck
// queue keeps surfacing rather than going silent.
export async function emitQueueSnapshot(trigger) {
  try {
    const all = await db.pending_images.toArray()

    const byStatus = { pending: 0, uploading: 0, failed: 0, "reauth-required": 0, uploaded: 0 }
    const byKind = { queued: 0, shadow: 0 }
    const byFailure = { lost: 0, failNet: 0, fail4xx: 0 }
    let oldestQueuedAt = null
    let queuedBytes = 0
    const responseIds = new Set()

    for (const record of all) {
      if (record.status in byStatus) byStatus[record.status]++
      if (record.kind in byKind) byKind[record.kind]++
      if (record.kind === "queued" && (!oldestQueuedAt || record.queuedAt < oldestQueuedAt)) {
        oldestQueuedAt = record.queuedAt
      }
      if (record.status === "failed") {
        if (isLost(record)) byFailure.lost++
        else if (record.failStatus === 0) byFailure.failNet++
        else byFailure.fail4xx++
      }
      if (record.kind === "queued") queuedBytes += record.sizeBytes || record.blob?.size || 0
      if (record.responseId != null) responseIds.add(String(record.responseId))
    }

    const liveIds = await _liveResponseIds([...responseIds])
    const noLive = all.filter(record =>
      _holdsUnsentWork(record) &&
      (isDirectUpload(record) || record.responseId == null || !liveIds.has(String(record.responseId)))
    ).length

    const oldestAgeHours = oldestQueuedAt
      ? Math.round((Date.now() - new Date(oldestQueuedAt).getTime()) / (60 * 60 * 1000))
      : 0
    const logsDropped = getDroppedLogCount()

    const state = {
      total:     all.length,
      pending:   byStatus.pending,
      uploading: byStatus.uploading,
      failed:    byStatus.failed,
      reauth:    byStatus["reauth-required"],
      uploaded:  byStatus.uploaded,
      queued:    byKind.queued,
      shadow:    byKind.shadow,
      oldestAgeHours,
      logsDropped,
      noLive,
      lost:      byFailure.lost,
      failNet:   byFailure.failNet,
      fail4xx:   byFailure.fail4xx,
      mb:        Math.round(queuedBytes / (1024 * 1024)),
      responses: responseIds.size
    }

    if (await _snapshotThrottled(state)) return

    const shouldWarn = oldestAgeHours >= SNAPSHOT_STALE_HOURS ||
      state.uploading > 0 ||
      state.failed > 0 ||
      state.lost > 0 ||
      state.failNet > 0 ||
      state.fail4xx > 0 ||
      state.noLive > 0 ||
      logsDropped > 0

    const write = shouldWarn ? logWarn : logInfo
    await write("upload", "Queue snapshot", { trigger, ...state }).catch(() => {})
  } catch { /* observability must never break the app */ }
}

// A row whose photo hasn't reached the server yet. Uploaded rows (shadow
// copies, and queued captures kept 24h as "Submitted" after an online
// completion) have nothing left to send, so an ended response isn't a problem
// for them.
function _holdsUnsentWork(record) {
  return !record.submittedAt && UNSENT_STATUSES.includes(record.status)
}
const UNSENT_STATUSES = ["pending", "uploading", "reauth-required", "failed"]

// Response ids (as strings) that still have somewhere to attach photos: a
// queued pending_responses row, or a cached_responses row not "ended". One
// anyOf per table. pending_images.responseId is a string; cached_responses
// holds a number, and pending_responses holds either (the editor queues its
// string value, the offline shell the cached number) -- so both forms are
// looked up.
async function _liveResponseIds(ids) {
  const live = new Set()
  if (ids.length === 0) return live

  const numeric = ids.map(Number).filter(Number.isFinite)
  const [cached, pending] = await Promise.all([
    numeric.length ? db.cached_responses.where("responseId").anyOf(numeric).toArray() : [],
    db.pending_responses.where("responseId").anyOf([...ids, ...numeric]).toArray()
  ])
  for (const row of cached)  if (row.status !== "ended") live.add(String(row.responseId))
  for (const row of pending) live.add(String(row.responseId))
  return live
}

async function _snapshotThrottled(state) {
  const fingerprint = JSON.stringify(state)
  const stored = await db.session.get(SNAPSHOT_SESSION_KEY).catch(() => null)

  const unchanged   = stored?.value?.fingerprint === fingerprint
  const withinWindow = stored?.value?.at && (Date.now() - stored.value.at) < SNAPSHOT_THROTTLE_MS

  if (unchanged && withinWindow) return true

  await db.session.put({ key: SNAPSHOT_SESSION_KEY, value: { fingerprint, at: Date.now() } }).catch(() => {})
  return false
}

// Explicitly discard one banked image by record id (My Photos delete button).
export async function discardImage(id) {
  await _purgeRecord(id)
}

// Discard banked rows matching the given survey-data URLs. Called when the
// tasker deletes a photo from a question (onClearFiles) -- an explicit discard,
// so the banked copy goes too, whatever its state. Matches on serverUrl (online
// captures) or localUrl (offline captures still showing a blob: URL).
export async function discardByUrls(urls) {
  if (!urls || urls.length === 0) return 0
  const wanted = new Set(urls)
  // Card #4097 -- a photo still waiting for an upload slot is dropped from
  // the background queue too. One already sending finishes its request.
  for (const job of [..._backgroundJobs.values()]) {
    if (!wanted.has(job.localUrl)) continue
    const waitingAt = _backgroundWaiting.indexOf(job.captureId)
    if (waitingAt >= 0) _backgroundWaiting.splice(waitingAt, 1)
    _backgroundJobs.delete(job.captureId)
  }
  const rows = await db.pending_images
    .filter(r => wanted.has(r.serverUrl) || wanted.has(r.localUrl))
    .toArray()
  for (const record of rows) {
    await _purgeRecord(record.id)
  }
  return rows.length
}

// Returns storage usage info for display in settings.
export async function storageUsage() {
  const pendingBytes = await _pendingBytes()
  const softCeiling  = await deviceSoftCeiling()
  let deviceUsage = null
  try {
    const estimate = await navigator.storage?.estimate()
    if (estimate) {
      deviceUsage = {
        usedMB:  (estimate.usage  / 1024 / 1024).toFixed(1),
        quotaMB: (estimate.quota  / 1024 / 1024).toFixed(0)
      }
    }
  } catch { /* not available */ }

  return {
    pendingBytes,
    pendingMB:        (pendingBytes / 1024 / 1024).toFixed(1),
    softCeilingBytes:  softCeiling,
    softCeilingMB:    (softCeiling / 1024 / 1024).toFixed(0),
    budgetMB:         (BUDGET_BYTES / 1024 / 1024).toFixed(0),
    overBudget:       pendingBytes >= softCeiling,
    deviceUsage
  }
}

// -- private --

// Writes a per-record log line at most once an hour (card #3995). `write` is one
// of the field_logger functions; the category is always "upload". Fire and
// forget, never throws.
function _logOncePerHour(key, write, message, detail) {
  shouldLogOnce(key)
    .then(first => { if (first) write("upload", message, detail).catch(() => {}) })
    .catch(() => {})
}

// One upload at a time (card #3995). Six retries started in the same second
// used to compete for a weak signal and all failed. Every retry-pass upload goes
// through here, so however many callers there are (the sync pass, the immediate
// retry after a vault failure, a service worker pass in this page) they queue
// up behind each other instead of racing. A failure never blocks the next one.
let _sequence = Promise.resolve()
function _inSequence(task) {
  const run = _sequence.then(task, task)
  _sequence = run.catch(() => {})
  return run
}

// Mirrors field_network_health.js's own private _now() (performance.now()
// where available, else Date.now()) -- duplicated rather than exported from
// that module, since it's a one-line timing helper and this file already
// imports the two classification functions (classifyFetchOutcome,
// isTimeoutError) that actually needed widening that module's public surface
// (Card #3492's XHR-progress evidence recording, below).
function _now() {
  return typeof performance !== "undefined" ? performance.now() : Date.now()
}

// Returns true if pending image bytes have crossed the device-aware soft ceiling.
// Never throws -- callers may warn but must still allow capture (W4).
async function _isOverSoftCeiling() {
  const total   = await _pendingBytes()
  const ceiling = await deviceSoftCeiling()
  return total >= ceiling
}

// A status write inside _uploadImage that could not land at all (safeSetStatus
// ok: false) throws, so the outer safety net resets the row rather than
// carrying on as if the write happened. The write's own error rides on cause.
async function _persistStatus(id, changes) {
  const result = await safeSetStatus(id, changes)
  if (!result.ok) {
    throw Object.assign(new Error("Photo status write failed"), { name: "StatusWriteError", cause: result.error })
  }
  return result
}

// Card #4914 -- converts every queued record still waiting on a HEIC decoder
// (needsHeicConversion: true, banked by field_offline_app_controller.js's
// _handleUpload when heic_decoder.js wasn't cached at capture time -- see
// queueImage's doc comment), using `convert`, a decode function the caller
// supplies (compressImageWithRetry from field_image_compressor.js).
//
// Deliberately takes `convert` as a parameter rather than importing
// field_image_compressor.js directly: that module computes WEBP_SUPPORTED at
// its OWN top level via `document.createElement("canvas")`, and THIS module
// is also bundled into the service worker (field_upload_sync.js imports
// syncImagesFor from here; field_service_worker.js imports that). A static
// import of field_image_compressor.js here would pull that top-level
// `document` call into the service worker's global scope, where `document`
// does not exist -- the exact #2763 class of bug
// field_service_worker_bundle_test.rb exists to catch ("evaluating the
// bundle threw a ReferenceError before install even fired"). Call this only
// from page-context code (a Stimulus controller, e.g. field_sync_controller.js
// before its own sync pass), never from the service worker.
//
// Returns how many photos converted this pass. A photo that still can't
// convert (decoder still unavailable, or a genuinely corrupt file) is left
// exactly as it was -- needsHeicConversion stays true, picked up again by the
// next call.
export async function convertPendingHeicPhotos(convert) {
  const pending = (await db.pending_images.toArray()).filter(r => r.needsHeicConversion)
  let converted = 0
  for (const record of pending) {
    const file = heldPhoto(record.blobKey) ||
      (isVaultRow(record)
        ? (await readFromVault(record.blobKey, { name: record.fileName || "image.heic" }).catch(() => null))?.file
        : null)
    if (!file) continue

    try {
      const result = await convert(file)
      await writeToVault(record.blobKey, result)
      holdPhotoInMemory(record.blobKey, result)
      await safeSetStatus(record.id, {
        needsHeicConversion: false,
        fileName:  result.name,
        sizeBytes: result.size
      })
      logInfo("upload", "Queued HEIC photo converted", {
        blobKey: record.blobKey, sizeBytes: result.size
      }).catch(() => {})
      converted++
    } catch (error) {
      logInfo("upload", "Queued HEIC photo not yet convertible", {
        blobKey: record.blobKey, reason: error?.reason || null, message: error?.message || null
      }).catch(() => {})
    }
  }
  return converted
}

// opts.source -- "capture" for the background uploader's first attempt
// (card #4097), so the "Photo upload from memory" line says where it came
// from; every other caller is a "retry".
async function _uploadImage(record, csrfToken, { source = "retry" } = {}) {
  // Card #3798 -- last-resort safety net around everything below. `stage`
  // tracks roughly where we are so an unexpected throw is diagnosable instead
  // of leaving the record stuck "uploading" with zero trace, which is how
  // this card's whole root-cause investigation started (no "Photo uploaded",
  // no "Photo upload failed", nothing -- ever, for a stuck record). This does
  // NOT replace any of the specific handling below -- every named branch
  // (blob unreadable, network failure/backoff, reauth, success, generic
  // failed/retry) still does its own status update and its own logging and
  // returns cleanly. This only fires for something none of those anticipated.
  //
  // Card #3827 -- every status write below goes through safeSetStatus, which
  // never throws: Dexie rewrites the whole row (Blob included) on update, and
  // a blob the browser can't serialise used to make the write itself throw.
  // `stage` names each write separately from the step before it, so a log
  // can tell "the blob probe failed" from "writing the result failed".
  let stage = "resolve_bytes"
  // Card #3994 -- the photo's bytes if they are still in memory from capture.
  // They are sent in preference to the stored copy, which an iPhone often can no
  // longer read back; the stored copy is only used once memory has lost the
  // photo (the app restarted).
  const memoryFile = heldPhoto(record.blobKey)
  const startedAt = _now()
  const attemptStartedAtMs = Date.now()
  try {
    // Card #3995 -- the bytes are in hand BEFORE the first status write. A
    // legacy row's embedded blob is read in full here (and held in memory),
    // because the status write below rewrites that row and on iOS a rewrite is
    // what makes a blob written by an earlier process unreadable. A vault row's
    // bytes come from pending_blobs, checked against their stored length and
    // hash.
    const resolved = await resolveSendBlob(record)
    const sendBlob = resolved.ok ? resolved.blob : null

    // A zero-byte blob reads "fine" (an empty slice is not an error) but can
    // never become a photo: the server would store an empty file (card #3990
    // addendum). Refuse to send it; same lost state as an unreadable blob, so
    // My Photos asks the tasker to retake it. The same goes for a read that
    // failed outright (Card #2601: an expired File handle passes instanceof
    // Blob and reports a size, but throws when serialised, and used to retry for
    // ever with an EMPTY request body).
    let probeError = null
    if (!resolved.ok) {
      probeError = Object.assign(new Error(resolved.errorMessage || `Photo bytes unavailable (${resolved.reason})`), {
        name: resolved.errorName || "BlobUnreadableError"
      })
    } else if (sendBlob.size === 0) {
      probeError = Object.assign(new Error("Blob is zero bytes"), { name: "EmptyBlobError" })
    }
    if (probeError) {
      stage = "persist_blob_unreadable"
      await _persistStatus(record.id, {
        status:     "failed",
        failedAt:   new Date().toISOString(),
        failStatus: BLOB_UNREADABLE_FAIL_STATUS
      })
      logError("upload", "Photo upload failed: blob unreadable", {
        surveyableId: record.surveyableId,
        name:         probeError?.name,
        message:      probeError?.message,
        reason:       resolved.reason || null,
        store:        isVaultRow(record) ? "vault" : "row"
      }).catch(() => {})
      // Card #4215 -- same signal as field_photo_vault.js's write-time
      // failure, for a read-time one: lets field_storage_health_controller
      // take and log a storage reading to correlate with this failure.
      try { window.dispatchEvent(new CustomEvent("field:vault-failed", { detail: { reason: resolved.reason || null, source: "upload" } })) } catch { /* non-browser context */ }
      return null
    }

    stage = "mark_uploading"
    // uploadStartedAt lets recoverStuckUploads() reclaim this record if the
    // page/SW dies before the fetch below ever resolves (Card #2419). This
    // write used to sit outside the try, so a throw here escaped entirely.
    // Card #3995 -- with the bytes already in hand a dropped legacy blob no
    // longer ends the attempt.
    await _persistStatus(record.id, { status: "uploading", uploadStartedAt: new Date().toISOString() })

    stage = "build_request"

    // Direct library uploads have no owning response -- the survey upload
    // endpoint 404s on surveyable_id 0 and would permanently fail the record, so
    // route them to the orphan-backup endpoint instead. Both return { url } on 2xx.
    const direct = isDirectUpload(record)
    const formData = new FormData()
    // Card #2864 -- id/context fields MUST be appended BEFORE the (multi-MB)
    // file field. Multipart form fields transmit in append order; if the
    // upload is interrupted mid-stream (several affected captures coincided
    // with "Connectivity entered degraded" events), the server can receive a
    // truncated body that never reaches the trailing fields -- exactly the
    // "surveyable params blank or missing" 404 this reorder is meant to avoid.
    // This can't fix a genuinely truncated file, but it keeps a partial upload
    // attributable, which is also what makes the server's OrphanedSync capture
    // (see FilesController#upload) actually reconcilable rather than just
    // another dead record.
    if (direct) {
      formData.append("direct", "true")
      // Queued direct uploads replayed by the SW carry the batch Place/Client
      // chosen in the My Photos form, same as the foreground backup path.
      if (record.placeId)  formData.append("place_id",  record.placeId)
      if (record.clientId) formData.append("client_id", record.clientId)
    } else {
      formData.append("surveyable_id",   record.surveyableId)
      formData.append("surveyable_type", record.surveyableType)
      // Card #3827 -- the question this photo answers, and the exact blob:
      // URL its answer holds locally. Banked on every capture since W1 but
      // never sent, so the server could neither patch the blob: slot
      // (substitute_blob_url! needs question_name) nor attach the photo to
      // an answer that never reached it.
      if (record.questionName) formData.append("question_name", record.questionName)
      if (record.localUrl)     formData.append("local_url",     record.localUrl)
    }
    // Card #3841 -- this capture's id (the queue row's blobKey), sent on every
    // attempt so a retry after a lost response (timeout, dropped connection
    // after the server stored the photo) gets that same Image back rather
    // than a second Image and a second answer entry. Before the file, like
    // every other identifier (card #2864).
    if (record.blobKey) formData.append("capture_id", record.blobKey)
    formData.append("file", sendBlob, sendBlob.name || "image.webp")

    const uploadUrl = direct ? "/api/v1/surveys/files/backup" : "/api/v1/surveys/files/upload"
    // A retry from memory never waits LESS than the stored-blob path would, but
    // at a Poor Signal place or a degraded connection it waits the longer
    // memory timeout (card #3994).
    //
    // Card #4097 -- a background row carries the place's Poor Signal flag from
    // the page Rails rendered, and every attempt for it uses the background
    // timeouts (60s, 90s at Poor Signal or Degraded, decision D3), whether the
    // bytes come from memory or the vault.
    let poorSignal = false
    let timeoutMs
    if (record.background) {
      poorSignal = record.poorSignal === true || await placeIsPoorSignal(record.placeId)
      timeoutMs = backgroundUploadTimeoutMs(poorSignal)
    } else if (memoryFile) {
      poorSignal = await placeIsPoorSignal(record.placeId)
      timeoutMs = Math.max(timeoutFor("image_upload", currentLevel()), memoryUploadTimeoutMs(poorSignal))
    } else {
      timeoutMs = timeoutFor("image_upload", currentLevel())
    }
    const reportMemory = (outcome, status) => {
      if (!memoryFile) return
      _logMemoryUpload(outcome, { source, timeoutMs, poorSignal, durationMs: _now() - startedAt, status, sizeBytes: sendBlob.size })
    }

    stage = "upload"
    let response
    try {
      // Foreground (page context): use XHR so upload.onprogress can report live
      // percentage to the pending-sync queue UI (Card #3492). Service worker
      // Background Sync replay keeps the original observedFetch/fetch path,
      // completely unchanged -- there's no UI to show progress to there, and
      // XHR support inside a SW is historically patchy across browsers (see
      // lib/xhr_upload.js's header comment). Both branches throw identically on
      // network failure/timeout, so the retry/backoff catch block below is
      // untouched by this split.
      if (supportsUploadProgress()) {
        // uploadWithProgress bypasses observedFetch (XHR has no fetch() to
        // wrap), so it must feed the SAME connectivity-evidence stream
        // observedFetch would have -- image uploads are a meaningfully large
        // slice of that evidence (ADR-0110), and this codebase's own review
        // caught that a bare XHR wrapper would silently drop it. Mirrors
        // observedFetch's try/catch shape exactly: one recordSample on success,
        // one on failure/timeout, never both.
        const startedAt = _now()
        try {
          response = await uploadWithProgress(uploadUrl, formData, {
            csrfToken,
            timeoutMs,
            onProgress: (loaded, total) => _dispatchUploadProgress(record, loaded, total)
          })
          recordSample({
            kind:       "image_upload",
            durationMs: Math.round(_now() - startedAt),
            outcome:    classifyFetchOutcome(response),
            sizeClass:  "large"
          })
        } catch (error) {
          // Card #4097 -- a background photo's request stopped because the
          // app was hidden is not connectivity evidence and not worth a second
          // transport now; the catch below re-arms it for when it is visible.
          if (record.background && _abortedWhileHidden(attemptStartedAtMs)) throw error
          // Card #3798 -- ONLY retry for a genuine abort (xhr_upload.js's new
          // onabort/loadend guard is what can produce this now -- before this
          // card, an abort just hung the promise forever, the actual root
          // cause). A plain network error or timeout is unchanged from
          // before: sampled once, then rethrown straight to the existing
          // backoff catch below, no retry -- a different transport wouldn't
          // fix a genuine connectivity failure any better, so there's no
          // point spending an extra request finding that out.
          if (error?.name !== "AbortError") {
            recordSample({
              kind:       "image_upload",
              durationMs: Math.round(_now() - startedAt),
              outcome:    isTimeoutError(error) ? "timeout" : "network_error",
              sizeClass:  "large"
            })
            throw error
          }
          // An abort on iOS Safari is often the browser reclaiming the
          // request mid-flight (backgrounding, tab suspend) rather than a
          // genuine connectivity failure -- worth ONE immediate retry
          // through a different transport before falling back to the normal
          // backoff flow. observedFetch has none of XHR's abort-prone
          // behaviour there. If this ALSO throws, it propagates to the catch
          // below, completely unchanged from before this card.
          //
          // Deliberately NOT recording a sample for the abort itself here --
          // observedFetch below records exactly one sample for WHATEVER this
          // retry's outcome turns out to be, so the existing "one
          // recordSample per attempt" invariant still holds even though this
          // attempt now has an internal retry hop.
          response = await observedFetch(uploadUrl, {
            method: "POST",
            headers: { "X-CSRF-Token": csrfToken, Accept: "application/json" },
            body: formData,
            signal: AbortSignal.timeout(timeoutMs)
          }, { kind: "image_upload", sizeClass: "large" })
        }
      } else {
        response = await observedFetch(uploadUrl, {
          method: "POST",
          headers: { "X-CSRF-Token": csrfToken, Accept: "application/json" },
          body: formData,
          signal: AbortSignal.timeout(timeoutMs)
        }, { kind: "image_upload", sizeClass: "large" })
      }
    } catch (uploadError) {
      if (record.background && _abortedWhileHidden(attemptStartedAtMs)) {
        // Card #4097 -- iOS stops requests in a hidden page. Back to pending
        // with no backoff and no retry counted; it starts again as soon as
        // the page is visible (_resumeBackgroundUploads).
        reportMemory("paused")
        stage = "persist_hidden_pause"
        await _persistStatus(record.id, { status: "pending", nextRetryAt: null })
        logEvent("upload", "Photo upload paused while the app was hidden", {
          surveyableId: record.surveyableId,
          blobKey:      record.blobKey,
          name:         uploadError?.name || null
        }).catch(() => {})
        return null
      }
      reportMemory(isTimeoutError(uploadError) ? "timeout" : "failed")
      // Network failure -- back off and retry. Never terminal (card #3827):
      // the wait caps at the last BACKOFF_MS slot and retryCount keeps
      // counting for telemetry.
      stage = "persist_network_backoff"
      const { retryCount, slot, backoffMs } = await _persistBackoff(record)
      // Card #2876 -- this was the one outcome branch in _uploadImage with zero
      // logging. A fetch()-level rejection (timeout, DNS failure, connection
      // reset) never resolves to a response, so it's distinct from the genuine
      // HTTP-status failure logged below ("Photo upload failed (${status})") --
      // no status ever exists on this path. capped: true once the wait has
      // reached its 1hr ceiling.
      logError("upload", "Photo upload failed: network error", {
        surveyableId: record.surveyableId,
        retryCount,
        backoffMs,
        capped: slot === BACKOFF_MS.length - 1
      }).catch(() => {})
      return null
    }

    stage = "classify_response"
    const outcome = await classifySyncResponse(response)

    if (outcome !== "success") reportMemory("failed", response.status)

    if (outcome === "reauth-required") {
      // Session expired mid-upload (Card #2417) -- distinct from a real 4xx so
      // it retries automatically on the next sync pass once the tasker signs
      // back in (syncImagesFor's pickup query includes "reauth-required"),
      // rather than sitting permanently "failed".
      stage = "persist_reauth"
      await _persistStatus(record.id, {
        status:     "reauth-required",
        reauthAt:   new Date().toISOString(),
        failStatus: response.status
      })
      logEvent("upload", "Photo upload paused — sign in required", {
        surveyableId: record.surveyableId, status: response.status
      }).catch(() => {})
      return null
    }

    if (outcome === "success") {
      stage = "parse_json"
      let data
      try {
        data = await response.json()
      } catch (error) {
        // classifySyncResponse already confirms a real JSON body before
        // returning "success", so this should be unreachable -- but a record
        // must NEVER be left stuck in "uploading" (Card #2419), so guard here
        // too and reset to pending rather than trust that guarantee blindly.
        // Card #3798 -- this branch used to reset with ZERO logging, one of
        // the confirmed-real silent paths this card's investigation found.
        stage = "persist_parse_failure"
        await _persistStatus(record.id, { status: "pending" })
        logError("upload", "Photo upload succeeded but response could not be parsed", {
          surveyableId: record.surveyableId,
          name:         error?.name,
          message:      error?.message
        }).catch(() => {})
        return null
      }
      // Card #3990 -- the server never refuses a photo for validation. It
      // answers 2xx and says what it did with the file:
      //   status "orphaned"        -- banked as an OrphanedSync for admin to
      //                               attribute; retain_local false. The photo
      //                               is safe, so purge it and do NOT mark the
      //                               record failed. Nothing to substitute into
      //                               the answer (it has no server URL).
      //   file_received === false  -- the file did not arrive (truncated body)
      //                               or was over the hard ceiling; keep the
      //                               local copy.
      if (data?.file_received === false || data?.retain_local === true) {
        stage = "persist_not_stored"
        reportMemory("failed", response.status)
        return await _keepAfterNotStored(record, data)
      }
      if (data?.status === "orphaned" && data?.retain_local === false) {
        stage = "persist_orphaned_purge"
        reportMemory("success", response.status)
        await _purgeRecord(record.id)
        logEvent("sync", "Photo banked as orphan", {
          surveyableId: record.surveyableId, orphanedSyncId: data.orphaned_sync_id || null,
          reason: data.reason || null, flags: data.flags || []
        }).catch(() => {})
        return null
      }

      stage = "persist_success"
      // Mark uploaded but DO NOT purge here -- the caller purges after it persists
      // the resolved URL (ADR-0048 §3). Retaining the record lets purgeStale()
      // reclaim it if the caller dies mid-persist.
      // attachedAt (card #3827) -- the server wrote this URL into the
      // response's answer itself (a blob: slot patched, or the photo
      // appended to its question). That is the durable record purge waits
      // for (isAttached below); without it the caller has to confirm another way.
      const attachedAt = (data.attached || data.substituted) ? new Date().toISOString() : null
      await _persistStatus(record.id, {
        status:     "uploaded",
        uploadedAt: new Date().toISOString(),
        serverUrl:  data.url,
        attachedAt
      })
      // The server has the photo: the in-memory copy has done its job.
      releasePhoto(record.blobKey)
      reportMemory("success", response.status)
      logEvent("upload", "Photo uploaded", {
        surveyableId: record.surveyableId,
        sizeBytes:    record.sizeBytes,
        attached:     !!attachedAt
      }).catch(() => {})
      return data.url
    }

    stage = "persist_failure"
    // "failed" (genuine 4xx) or "retry" (5xx) -- both permanent-fail here today.
    // The 5xx-as-permanent-failure behaviour for images predates this batch and
    // is left unchanged (out of scope for cards #2417-#2434); only the reauth
    // carve-out above is new.
    await _persistStatus(record.id, {
      status:      "failed",
      failedAt:    new Date().toISOString(),
      failStatus:  response.status
    })

    // Card #2864 -- an older server answers 404 with an orphaned_sync_id when
    // it banked a dead-letter copy of this attempt (FilesController#upload).
    // Since card #3990 the server answers 2xx for those (handled above), so
    // this only fires against a not-yet-deployed server. Kept so the log still
    // reads "recovered" there.
    let orphanedSyncId = null
    try {
      const data = await response.json()
      orphanedSyncId = data?.orphaned_sync_id || null
    } catch { /* no JSON body, or already consumed -- fall through to generic log */ }

    if (orphanedSyncId) {
      logEvent("sync", "Orphaned sync created", {
        surveyableId: record.surveyableId, orphanedSyncId
      }).catch(() => {})
    } else {
      logError("upload", `Photo upload failed (${response.status})`, {
        surveyableId: record.surveyableId,
        status:       response.status
      }).catch(() => {})
    }
    return null
  } catch (error) {
    // Card #3798 -- the last-resort net described above. Reached only when
    // something escaped every specific branch above (e.g. a Dexie write
    // failing mid-persist, right after a confirmed-success response). Reset
    // to "pending" rather than leave the record stranded in "uploading" --
    // the next sync pass retries it -- and log enough (stage + error
    // identity) to diagnose what actually broke, since this is exactly the
    // kind of silence this card exists to close off. Card #3827 -- the reset
    // is blob-safe and its own outcome is logged separately from the error
    // that brought us here, so a failed write no longer hides behind `stage`.
    const reset = await safeSetStatus(record.id, { status: "pending" })
    _logOncePerHour(`abort:${record.blobKey}:${stage}`, logError, "Photo upload aborted unexpectedly", {
      surveyableId: record.surveyableId,
      stage,
      name:         error?.name,
      message:      error?.message,
      writeName:    error?.cause?.name,
      writeMessage: error?.cause?.message,
      resetOk:      reset.ok,
      blobDropped:  reset.blobDropped
    })
    return null
  }
}

// Dispatches a `field:upload-progress` window event so page-context UI (the
// pending-sync queue rows, field_pending_queue_controller.js) can reflect a
// live upload percentage without polling IndexedDB (Card #3492). Only ever
// reached from the foreground XHR branch above, so `window` is always
// present here -- the typeof guard is just defensive, matching the same
// pattern field_network_health.js already uses for its own event dispatch.
function _dispatchUploadProgress(record, loaded, total) {
  if (typeof window === "undefined" || !total) return
  const pct = Math.min(100, Math.round((loaded / total) * 100))
  try {
    window.dispatchEvent(new CustomEvent("field:upload-progress", {
      detail: {
        recordId:     record.id,
        blobKey:      record.blobKey,
        surveyableId: record.surveyableId,
        responseId:   record.responseId,
        loaded,
        total,
        pct
      }
    }))
  } catch { /* no-op in a context without CustomEvent */ }
}

// Backoff write shared by the network-failure catch and the "server did not
// receive the file" outcome. Never terminal: the wait caps at the last
// BACKOFF_MS slot (1hr) and retryCount keeps counting for telemetry.
async function _persistBackoff(record) {
  const retryCount = (record.retryCount || 0) + 1
  const slot       = Math.min(retryCount - 1, BACKOFF_MS.length - 1)
  // Card #4097 (decision D7) -- background photos use Full Jitter: a random
  // wait up to the ladder's value (never under JITTER_FLOOR_MS), so photos
  // that failed together do not all retry together. Other rows keep the
  // fixed ladder.
  const backoffMs  = record.background
    ? Math.max(JITTER_FLOOR_MS, Math.round(Math.random() * BACKOFF_MS[slot]))
    : BACKOFF_MS[slot]
  const nextRetry  = new Date(Date.now() + backoffMs).toISOString()
  await _persistStatus(record.id, { status: "pending", retryCount, nextRetryAt: nextRetry })
  return { retryCount, slot, backoffMs }
}

// 2xx with file_received:false / retain_local:true (card #3990). The device
// keeps its copy. A truncated upload backs off and retries like a network
// failure; a file over the server's hard ceiling can never succeed, so it is
// parked failed (TOO_LARGE_FAIL_STATUS) rather than retried forever.
async function _keepAfterNotStored(record, data) {
  if (data?.reason === "too_large") {
    await _persistStatus(record.id, {
      status:     "failed",
      failedAt:   new Date().toISOString(),
      failStatus: TOO_LARGE_FAIL_STATUS
    })
    logError("upload", "Photo too large for the server, kept on device", {
      surveyableId: record.surveyableId, sizeBytes: record.sizeBytes || null
    }).catch(() => {})
    return null
  }

  const { retryCount, backoffMs } = await _persistBackoff(record)
  logError("upload", "Server did not receive the photo file, will retry", {
    surveyableId: record.surveyableId, reason: data?.reason || null, retryCount, backoffMs
  }).catch(() => {})
  return null
}

async function _purgeRecord(id) {
  const record = await db.pending_images.get(id)
  if (record?.localUrl) {
    try { URL.revokeObjectURL(record.localUrl) } catch { /* ok */ }
  }
  if (record?.blobKey) releasePhoto(record.blobKey)
  // The vault bytes go with their row, in one transaction.
  await db.transaction("rw", db.pending_images, db.pending_blobs, async () => {
    if (record?.blobKey) await db.pending_blobs.delete(record.blobKey)
    await db.pending_images.delete(id)
  })
}

// Vault bytes whose pending_images row is gone (a tab that died between the two
// writes at capture, or a purge that only half finished). Only rows older than
// ORPHAN_BLOB_GRACE_MS are touched, so a photo being captured right now is
// never swept. Returns how many were removed. Never throws.
const ORPHAN_BLOB_GRACE_MS = 60 * 60 * 1000
export async function purgeOrphanedVaultBlobs() {
  try {
    // Keys only first (no photo bytes are loaded), then the age of the few
    // candidates.
    const owned = new Set(await db.pending_images.orderBy("blobKey").keys())
    const unowned = (await db.pending_blobs.toCollection().primaryKeys()).filter(key => !owned.has(key))
    if (unowned.length === 0) return 0

    const cutoff = new Date(Date.now() - ORPHAN_BLOB_GRACE_MS).toISOString()
    const rows = await db.pending_blobs.bulkGet(unowned)
    const orphans = unowned.filter((key, index) => !rows[index]?.createdAt || rows[index].createdAt < cutoff)
    for (const key of orphans) await deleteFromVault(key)

    if (orphans.length > 0) logInfo("upload", "Purged orphaned vault bytes", { count: orphans.length }).catch(() => {})
    return orphans.length
  } catch {
    return 0
  }
}

async function _pendingBytes() {
  const records = await db.pending_images
    .filter(r => r.status === "pending" || r.status === "uploading")
    .toArray()
  return records.reduce((sum, r) => sum + (r.sizeBytes || 0), 0)
}
